import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Send, Sparkles, Plus, Paperclip, FileText, Mic, Pin } from 'lucide-react';
import useAuthStore from '../store/authStore';
import api from '../api/client';
import { sendMessage, createRecord, updateRecord, executeTransition, uploadDocument, confirmDocumentCategory, saveDocumentTags, getHistory, searchHistory, deleteDocument, createTask, logAction, updateTask, createIntake } from '../api/chatAI';
import DemandLetterModal from './DemandLetterModal';

function stripTrailingJson(text) {
  if (!text) return text;
  // Remove raw JSON that appears as plain text
  let result = text;
  const jsonStart = result.lastIndexOf('\n{"intent"');
  if (jsonStart > 0) result = result.substring(0, jsonStart).trim();
  // Remove JSON wrapped in markdown code blocks (```json ... ``` or ``` ... ```)
  result = result.replace(/\n?```json\s*\{[\s\S]*?\}\s*```/gi, '').trim();
  result = result.replace(/\n?```\s*\{[\s\S]*?\}\s*```/gi, '').trim();
  // Remove trailing bare JSON object
  result = result.replace(/\n?\{[\s\S]*"intent"[\s\S]*\}$/gi, '').trim();
  return result;
}

function renderMarkdown(text) {
  text = stripTrailingJson(text);
  if (!text) return null;
  const lines = text.split('\n');
  const elements = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i++; continue; }
    if (/^[\-\*]\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^[\-\*]\s/.test(lines[i])) {
        items.push(lines[i].replace(/^[\-\*]\s/, ''));
        i++;
      }
      elements.push(<ul key={elements.length} className="list-disc list-inside ml-2 my-1 space-y-0.5 text-gray-300">{items.map((it, j) => <li key={j}>{it}</li>)}</ul>);
      continue;
    }
    if (/^\d+\.\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s/, ''));
        i++;
      }
      elements.push(<ol key={elements.length} className="list-decimal list-inside ml-2 my-1 space-y-0.5 text-gray-300">{items.map((it, j) => <li key={j}>{it}</li>)}</ol>);
      continue;
    }
    const bold = line.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    elements.push(<p key={elements.length} className="my-0.5 text-gray-200" dangerouslySetInnerHTML={{ __html: bold }} />);
    i++;
  }
  return elements;
}

function formatMsgTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' +
         d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function ActionConfirmCard({ action, onConfirm, onDismiss, loading }) {
  const hasFields = action.field_updates?.length > 0;
  const hasExt = action.extended_data && Object.keys(action.extended_data).length > 0;

    return (
    <div className="my-2 rounded-xl border border-amber-700/50 bg-amber-950/30 overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-amber-700/30 bg-amber-900/20">
        <span className="text-sm">✏️</span>
        <span className="text-xs font-semibold text-amber-300">Proposed Update</span>
        {action.narrative && <span className="text-xs text-amber-400/70 italic truncate flex-1">{action.narrative}</span>}
      </div>
      {hasFields && (
        <div className="px-3 py-2 space-y-1.5">
          <p className="text-[10px] text-amber-400/60 font-medium uppercase tracking-wide">Field Changes</p>
          {action.field_updates.map((fu, i) => (
            <div key={i} className="flex items-center justify-between text-xs">
              <span className="text-gray-400">{fu.field_name}</span>
              <span className="text-gray-200 bg-gray-800 px-2 py-0.5 rounded font-mono text-[11px]">{fu.display_value || String(fu.value)}</span>
            </div>
          ))}
        </div>
      )}
      {hasExt && (
        <div className="px-3 py-2 border-t border-amber-700/20 space-y-1">
          <p className="text-[10px] text-amber-400/60 font-medium uppercase tracking-wide">Flags</p>
          {Object.entries(action.extended_data).map(([k, v]) => (
            <div key={k} className="flex justify-between text-xs">
              <span className="text-gray-500">{k.replace(/_/g, ' ')}</span>
              <span className="text-amber-300">{String(v)}</span>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2 px-3 py-2 border-t border-amber-700/20">
        <button onClick={onConfirm} disabled={loading}
          className="flex-1 py-1.5 text-xs font-semibold text-white bg-green-600 hover:bg-green-700 rounded-lg disabled:opacity-50 transition-colors">
          {loading ? 'Saving...' : '✓ Confirm'}
        </button>
        <button onClick={onDismiss} disabled={loading}
          className="flex-1 py-1.5 text-xs text-gray-400 bg-gray-800 hover:bg-gray-700 rounded-lg disabled:opacity-50 transition-colors">
          Dismiss
        </button>
      </div>
    </div>
  );
}

function DuplicateCard({ state, onUseExisting, onCreateAnyway, onCancel, loading }) {
  const [showReason, setShowReason] = React.useState(false);
  const [reason, setReason] = React.useState('');
  const isHard = state.level === 'hard';


    return (
    <div className="relative">
      {/* Main duplicate card — always visible */}
      <div className="rounded-2xl border border-gray-700 bg-gray-900 overflow-hidden">
        <div className="px-4 py-3 border-l-4 flex items-center justify-between" style={{ borderLeftColor: isHard ? '#c0303a' : '#d4900a', background: isHard ? '#1e0a0a' : '#1e1800' }}>
          <div>
            <p className="text-xs font-semibold" style={{ color: isHard ? '#e04050' : '#e8a020' }}>
              {isHard ? '🔴' : '🟡'} Possible Duplicate Found
            </p>
            <p className="text-[10px] text-gray-500 mt-0.5">We found a record that closely matches this entry</p>
          </div>
          <button onClick={onCancel} className="text-gray-500 hover:text-gray-300 text-lg leading-none p-1 transition-colors">×</button>
        </div>
        <div className="px-4 py-3 space-y-2">
          {state.matches.slice(0, 3).map((m, i) => (
            <div key={i} className="bg-gray-800/60 rounded-xl px-3 py-2.5">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold text-white">{m.title}</span>
                <div className="flex items-center gap-1.5">
                  {m.reference_number && <span className="text-[10px] text-gray-500 font-mono">{m.reference_number}</span>}
                  <span className="text-[10px] rounded-full font-medium" style={{ background: m.level === 'hard' ? '#2a0d0d' : '#1e1800', color: m.level === 'hard' ? '#e04050' : '#e8a020', padding: '2px 8px', borderRadius: '20px' }}>{m.confidence}%</span>
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${m.level === 'hard' ? 'bg-red-500' : 'bg-yellow-500'}`} />
                </div>
              </div>
              <p className="text-[10px] text-gray-400">
                Matched on: <span style={{ color: '#c9a84c' }}>{m.matching_fields.join(' · ')}</span>
                {m.stage_name ? ` · ${m.stage_name}` : ''}
              </p>
            </div>
          ))}
        </div>
        <div className="flex gap-2 px-4 py-3 border-t border-gray-800">
          <button onClick={onUseExisting} disabled={loading}
            className="flex-1 text-xs disabled:opacity-50 transition-colors"
            style={{ background: 'linear-gradient(135deg, #1a6b8a, #0d4f6b)', color: '#a8dff0', border: '1px solid #1e7a9e', borderRadius: '12px', padding: '10px 0', fontWeight: 600, fontSize: '13px', letterSpacing: '0.02em', cursor: 'pointer' }}>
            🔗 Use Existing
          </button>
          <button onClick={() => {
              if (isHard) setShowReason(true);
              else onCreateAnyway('Confirmed different record');
            }}
            disabled={loading}
            className="flex-1 text-xs disabled:opacity-50 transition-colors"
            style={{ background: '#1a1b1f', color: '#a09880', border: '1px solid #3a3620', borderRadius: '12px', padding: '10px 0', fontWeight: 600, fontSize: '13px', letterSpacing: '0.02em', cursor: 'pointer' }}>
            👤 Create Anyway
          </button>
          <button onClick={onCancel}
            className="text-xs transition-colors"
            style={{ background: 'transparent', color: '#d94f4f', border: '1px solid #7a2424', borderRadius: '12px', padding: '10px 16px', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}>
            ✕ Cancel
          </button>
        </div>
      </div>

      {/* Reason Modal — overlays the card when showReason is true */}
      {showReason && (
        <div className="absolute inset-0 bg-gray-950/90 backdrop-blur-sm rounded-2xl flex items-center justify-center p-4 z-10">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full shadow-2xl overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between">
              <p className="text-xs font-semibold text-red-300">🔴 Override Required</p>
              <button onClick={() => { setShowReason(false); setReason(''); }}
                className="text-gray-500 hover:text-gray-300 text-lg leading-none transition-colors">×</button>
            </div>
            <div className="px-4 py-4 space-y-3">
              <p className="text-xs text-gray-300 font-medium">Why is this a different record?</p>
              <p className="text-[10px] text-gray-500">
                A note is required to override a hard duplicate. This will be logged for audit purposes.
              </p>
              <textarea
                autoFocus
                rows={3}
                placeholder="e.g. Same name but confirmed different person — client verified by photo ID..."
                value={reason}
                onChange={e => setReason(e.target.value)}
                className="w-full bg-gray-800 border border-gray-600 focus:border-amber-600 text-gray-200 text-xs rounded-xl px-3 py-2.5 focus:outline-none resize-none transition-colors"
              />
              <p className={`text-[10px] transition-colors ${reason.length >= 10 ? 'text-green-400' : 'text-gray-500'}`}>
                {reason.length} / 10 chars minimum{reason.length >= 10 && ' ✓'}
              </p>
            </div>
            <div className="flex gap-2 px-4 pb-4">
              <button onClick={() => { setShowReason(false); setReason(''); }}
                className="flex-1 py-2 text-xs rounded-xl transition-colors"
                style={{ background: '#1a1b1f', color: '#a09880', border: '1px solid #3a3620', borderRadius: '12px', padding: '10px 0', fontSize: '13px', cursor: 'pointer' }}>
                ← Back
              </button>
              <button
                onClick={() => { onCreateAnyway(reason); setShowReason(false); setReason(''); }}
                disabled={reason.length < 10 || loading}
                className="flex-1 py-2 text-xs font-semibold rounded-xl disabled:opacity-50 transition-colors"
                style={{ background: 'linear-gradient(135deg, #7a1f26, #5a1519)', color: '#f0a0a8', border: '1px solid #9e2a35', borderRadius: '12px', padding: '10px 0', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}>
                {loading ? 'Creating...' : 'Confirm & Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const GLOBAL_PROMPTS = ['Show me urgent cases', 'New intake', "What's due today?", 'Brief me on a case'];
const CASE_PROMPTS = ['Brief me on this case', 'What documents are missing?', 'Draft demand letter', 'What are the next steps?'];

function AutoCreateTasks({ msg, selectedRecord, conversationId, onDone }) {
  const allTasks = React.useMemo(() => [
    { title: msg.action.title, description: msg.action.description, priority: msg.action.priority, due_date_text: msg.action.due_date_text || msg.action.due_date, recurrence_rule: msg.action.recurrence_rule, recurrence_end_text: msg.action.recurrence_end_text },
    ...(msg.action.additional_tasks || []).map(t => ({...t, due_date_text: t.due_date_text || t.due_date})),
  ].filter(t => t.title), []);

  const missingDates = allTasks.filter(t => !t.due_date_text);
  // Show recurrence info
  const hasRecurrence = allTasks.some(t => t.recurrence_rule);
  // Always show confirmation card first — prevents accidental task creation from ambiguous intent
  const initialStatus = hasRecurrence ? 'confirm_recurring' : 'confirm';
  const [status, setStatus] = React.useState(initialStatus);
  const [dueDates, setDueDates] = React.useState({});
  const [current, setCurrent] = React.useState(0);

  const executeTasks = React.useCallback(async (tasks) => {
    setStatus('creating');
    const created = [];
    for (let i = 0; i < tasks.length; i++) {
      setCurrent(i + 1);
      try {
        const r = await fetch('/api/chat-ai/create-task', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('lf_token')}` },
          body: JSON.stringify({
            record_id: selectedRecord?.record_id,
            title: tasks[i].title,
            description: tasks[i].description || null,
            priority: tasks[i].priority || 'normal',
            due_date_text: tasks[i].due_date_text || tasks[i].due_date || null,
            recurrence_rule: tasks[i].recurrence_rule || null,
            recurrence_end_text: tasks[i].recurrence_end_text || null,
            conversation_id: conversationId,
          })
        });
        const data = await r.json();
        if (data.task) {
          created.push({...data.task, recurrence_rule: tasks[i].recurrence_rule, recurrence_end_text: tasks[i].recurrence_end_text});
          // Log to history
          if (conversationId) {
            fetch('/api/chat-ai/log-action', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('lf_token')}` },
              body: JSON.stringify({ conversation_id: conversationId, status: 'confirmed', summary: `Task created: "${data.task.title}"${tasks[i].recurrence_rule ? ' recurring ' + tasks[i].recurrence_rule : ''}${data.task.due_date ? ' due ' + data.task.due_date.split('T')[0] : ''}` })
            }).catch(() => {});
          }
        }
      } catch(e) { console.error('Task create error:', e); }
      await new Promise(res => setTimeout(res, 200));
    }
    setStatus('done');
    onDone(created);
  }, [selectedRecord, conversationId]);

  // Auto-start if no missing dates
  React.useEffect(() => {
    if (status === 'creating') executeTasks(allTasks);
  }, [status]);

  if (status === 'done') return null;

  const total = allTasks.length;

  if (status === 'confirm') {
    const t = allTasks[0];
    const extra = allTasks.length > 1 ? ` + ${allTasks.length - 1} more task${allTasks.length > 2 ? 's' : ''}` : '';
    return (
      <div className="mt-2 bg-gray-900 border border-green-800/40 rounded-xl p-3">
        <p className="text-xs text-green-300 font-medium mb-2">Create Task{allTasks.length > 1 ? 's' : ''}?</p>
        <p className="text-xs text-white font-medium mb-1">{t.title}{extra}</p>
        <div className="space-y-0.5 text-[10px] text-gray-400">
          {t.due_date_text && <p>Due: <span className="text-gray-200">{t.due_date_text}</span></p>}
          {t.priority && t.priority !== 'normal' && <p>Priority: <span className="text-yellow-300">{t.priority}</span></p>}
          {t.description && <p className="italic truncate text-gray-500">{t.description}</p>}
        </div>
        <div className="flex gap-2 mt-3">
          <button
            onClick={() => setStatus(missingDates.length > 0 ? 'needs_dates' : 'creating')}
            className="flex-1 py-1.5 text-xs font-semibold text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors">
            ✓ Create Task{allTasks.length > 1 ? 's' : ''}
          </button>
          <button
            onClick={() => onDone([])}
            className="px-3 py-1.5 text-xs text-gray-400 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors">
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (status === 'confirm_recurring') {
    const rt = allTasks[0];
    return (
      <div className="mt-2 bg-gray-900 rounded-xl p-3" style={{ border: '1px solid #3d3418' }}>
        <p className="text-xs font-medium mb-2" style={{ color: '#c9a84c' }}>🔄 Recurring Task</p>
        <p className="text-xs text-white font-medium mb-1">{rt.title}</p>
        <div className="space-y-1 text-[10px] text-gray-400">
          <p>Repeats: <span style={{ color: '#c9a84c' }}>{rt.recurrence_rule?.replace('WEEKLY:', 'Every ').replace('MON','Monday').replace('TUE','Tuesday').replace('WED','Wednesday').replace('THU','Thursday').replace('FRI','Friday')}</span></p>
          <p>First due: <span className="text-gray-200">{rt.due_date_text}</span></p>
          {rt.recurrence_end_text && <p>Until: <span className="text-gray-200">{rt.recurrence_end_text}</span></p>}
        </div>
        <div className="flex gap-2 mt-3">
          <button onClick={() => setStatus(missingDates.length > 0 ? 'needs_dates' : 'creating')}
            className="flex-1 py-1.5 text-xs font-semibold rounded-lg transition-colors"
            style={{ background: 'linear-gradient(135deg, #c9a84c, #a67c32)', color: '#fff8e6', border: '1px solid #d4b060' }}>
            ✓ Create Recurring Task
          </button>
          <button onClick={() => onDone([])}
            className="px-3 py-1.5 text-xs text-gray-400 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors">
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (status === 'needs_dates') {
    return (
      <div className="mt-2 bg-gray-900 border border-yellow-800/40 rounded-xl p-3">
        <p className="text-xs text-yellow-300 font-medium mb-2">⚠️ {missingDates.length} task{missingDates.length !== 1 ? 's' : ''} need a due date before creating:</p>
        <div className="space-y-2">
          {allTasks.map((t, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="text-xs text-gray-300 flex-1 truncate">{t.title}</span>
              {t.due_date_text ? (
                <span className="text-xs text-green-400 flex-shrink-0">{t.due_date_text}</span>
              ) : (
                <input type="date"
                  value={dueDates[i] || ''}
                  onChange={e => setDueDates(prev => ({ ...prev, [i]: e.target.value }))}
                  className="bg-gray-800 border border-gray-700 text-gray-200 text-xs rounded px-2 py-1 focus:outline-none focus:border-yellow-500 flex-shrink-0"
                />
              )}
            </div>
          ))}
        </div>
        <div className="flex gap-2 mt-3">
          <button
            onClick={() => {
              const tasksWithDates = allTasks.map((t, i) => ({
                ...t,
                due_date_text: t.due_date_text || dueDates[i] || null,
              }));
              executeTasks(tasksWithDates);
            }}
            disabled={missingDates.some((_, i) => {
              const idx = allTasks.findIndex(t => !t.due_date && allTasks.indexOf(t) >= 0);
              return !dueDates[allTasks.indexOf(missingDates[i])];
            })}
            className="flex-1 py-1.5 text-xs font-semibold text-white bg-green-600 hover:bg-green-700 rounded-lg disabled:opacity-50 transition-colors">
            Create {total} Task{total !== 1 ? 's' : ''}
          </button>
          <button
            onClick={() => onDone([])}
            className="px-3 py-1.5 text-xs text-gray-400 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors">
            Cancel
          </button>
        </div>
      </div>
    );
  }


    return (
    <div className="mt-2 bg-gray-900 border border-green-800/40 rounded-xl p-3">
      <p className="text-xs text-green-300 font-medium">Creating {total} task{total !== 1 ? 's' : ''}...</p>
      <p className="text-xs text-gray-400 mt-1">{current}/{total} — {allTasks[current - 1]?.title || ''}</p>
      {hasRecurrence && <p className="text-[10px] mt-0.5" style={{ color: '#c9a84c' }}>🔄 Recurring: {allTasks[0]?.recurrence_rule}{allTasks[0]?.recurrence_end_text ? ' until ' + allTasks[0].recurrence_end_text : ''}</p>}
      <div className="mt-2 bg-gray-800 rounded-full h-1.5">
        <div className="bg-green-500 h-1.5 rounded-full transition-all" style={{ width: `${(current/total)*100}%` }} />
      </div>
    </div>
  );
}

export default function ChatPanel({ selectedRecord, onSelectRecord, onRefreshList, onRefreshProfile }) {
  const { user } = useAuthStore();
  const currentUserName = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email : 'You';
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [executingAction, setExecutingAction] = useState(null);
  const [executingUpdate, setExecutingUpdate] = useState(false);
  const [executingTransition, setExecutingTransition] = useState(false);
  const [executingTask, setExecutingTask] = useState(false);
  const [taskQueue, setTaskQueue] = useState([]);
  const [shareCaseState, setShareCaseState] = useState(null); // { recipient_name, recipient_type, recipient_email, resolved_user, multiple_matches }
  const [smsState, setSmsState] = useState(null); // { recordId, phone, message, recipientName }
  const [callState, setCallState] = useState(null); // { recordId, phone, recipientName }
  const [disambiguateState, setDisambiguateState] = useState(null);
  const [addPartyState, setAddPartyState] = useState(null);   // { recordId, contactId, contactName, role, notFound, candidates }
  const [triggerState, setTriggerState] = useState(null); // MANAGE_TRIGGERS confirm card
  const [demandLetterState, setDemandLetterState] = useState(null);
  const [demandLetterOpen, setDemandLetterOpen] = useState(false);
  const [triggerExecuting, setTriggerExecuting] = useState(false);
  const [removePartyState, setRemovePartyState] = useState(null); // { recordId, contactName, role }
  const [partyExecuting, setPartyExecuting] = useState(false);
  const [smsSending, setSmsSending] = useState(false);
  const [callDialing, setCallDialing] = useState(false);

  const stripMarkdown = (text) => (text || '')
    .replace(/\*\*(.*?)\*\*/g, '$1')  // bold
    .replace(/\*(.*?)\*/g, '$1')       // italic
    .replace(/#{1,6}\s/g, '')          // headers
    .replace(/`(.*?)`/g, '$1');        // inline code

  const handleDisambiguateSelect = async (option) => {
    if (!disambiguateState) return;
    const savedMessage = disambiguateState.savedMessage;
    setDisambiguateState(null);
    if (onSelectRecord) {
      onSelectRecord({ record_id: option.record_id, title: option.title, record_type_name: option.record_type });
    }
    if (savedMessage) {
      try {
        const token = localStorage.getItem('lf_token');
        const res = await fetch('/api/chat-ai/send-sms', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ recordId: option.record_id, message: savedMessage }),
        });
        const data = await res.json();
        if (data.needsPhoneSelection && data.phones?.length) {
          const phones = data.phones;
          setSmsState({
            recordId: option.record_id,
            phone: phones.length === 1 ? phones[0].number : null,
            phones: phones.length > 1 ? phones : null,
            selectedPhone: phones.length === 1 ? 0 : null,
            message: savedMessage,
            recipientName: option.title.split(' ')[0],
          });
        } else if (data.ok) {
          setMessages(prev => [...prev, {
            id: 'sms-ok-' + Date.now(), role: 'assistant',
            content: `✅ SMS sent to ${option.title.split(' ')[0]}.`,
            created_at: new Date().toISOString(),
          }]);
        }
      } catch (e) {
        console.error('[disambiguate sms]', e);
      }
    }

  };

  const handleAddParty = async (overrideContactId) => {
    if (!addPartyState || partyExecuting) return;
    const contactId = overrideContactId || addPartyState.contactId;
    setPartyExecuting(true);
    try {
      const token = localStorage.getItem('lf_token');
      const res = await fetch('/api/chat-ai/add-party', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ recordId: addPartyState.recordId, contactId, contactName: addPartyState.contactName, role: addPartyState.role }),
      });
      const data = await res.json();
      if (data.not_found) {
        setAddPartyState(prev => ({ ...prev, notFound: true }));
        setPartyExecuting(false);
        return;
      }
      if (data.candidates) {
        setAddPartyState(prev => ({ ...prev, candidates: data.candidates }));
        setPartyExecuting(false);
        return;
      }
      if (!res.ok) {
        setMessages(prev => [...prev, { id: 'party-err-' + Date.now(), role: 'assistant', content: '❌ ' + (data.error || 'Failed to add party'), created_at: new Date().toISOString() }]);
      } else {
        setMessages(prev => [...prev, { id: 'party-ok-' + Date.now(), role: 'assistant', content: `✅ ${data.contactName || addPartyState.contactName} added as ${addPartyState.role || 'party'}.`, created_at: new Date().toISOString() }]);
        setTimeout(() => { if (onRefreshProfile) onRefreshProfile(); }, 500);
      }
    } catch (err) {
      console.error('[AddParty]', err);
    } finally {
      setPartyExecuting(false);
      setAddPartyState(null);
    }
  };

  const handleRemoveParty = async () => {
    if (!removePartyState || partyExecuting) return;
    setPartyExecuting(true);
    try {
      const token = localStorage.getItem('lf_token');
      const res = await fetch('/api/chat-ai/remove-party', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ recordId: removePartyState.recordId, contactName: removePartyState.contactName, role: removePartyState.role }),
      });
      const data = await res.json();
      if (!res.ok) {
        setMessages(prev => [...prev, { id: 'party-err-' + Date.now(), role: 'assistant', content: '❌ ' + (data.error || 'Failed to remove party'), created_at: new Date().toISOString() }]);
      } else {
        setMessages(prev => [...prev, { id: 'party-ok-' + Date.now(), role: 'assistant', content: `✅ ${data.removed || removePartyState.contactName} removed from this case.`, created_at: new Date().toISOString() }]);
        setTimeout(() => { if (onRefreshProfile) onRefreshProfile(); }, 500);
      }
    } catch (err) {
      console.error('[RemoveParty]', err);
    } finally {
      setPartyExecuting(false);
      setRemovePartyState(null);
    }
  };

  const handleSendSMS = async () => {
    if (!smsState || smsSending) return;
    setSmsSending(true);
    try {
      const token = localStorage.getItem('lf_token');
      const res = await fetch('/api/chat-ai/send-sms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ recordId: smsState.recordId, phone: smsState.phone, message: smsState.message, recipientName: smsState.recipientName }),
      });
      const data = await res.json();
      if (data.needsPhoneSelection) {
        // Backend found multiple phones — show selector
        setSmsState(prev => ({ ...prev, phones: data.phones, phone: null, selectedPhone: null }));
        setSmsSending(false);
        return;
      }
      if (!res.ok) {
        setMessages(prev => [...prev, { id: 'sms-err-' + Date.now(), role: 'assistant', content: '❌ ' + (data.error || 'Failed to send SMS'), created_at: new Date().toISOString() }]);
      } else {
        setMessages(prev => [...prev, { id: 'sms-ok-' + Date.now(), role: 'assistant', content: '✅ SMS sent to ' + smsState.recipientName + ' (' + (smsState.phone || data.to) + ').', created_at: new Date().toISOString() }]);
      }
    } catch (err) {
      console.error('[SendSMS]', err);
    } finally {
      setSmsSending(false);
      setSmsState(null);
    }
  };

  const handleInitiateCall = async () => {
    if (!callState || callDialing) return;
    setCallDialing(true);
    try {
      const token = localStorage.getItem('lf_token');
      const res = await fetch('/api/chat-ai/initiate-call', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ recordId: callState.recordId, phone: callState.phone, recipientName: callState.recipientName }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.code === 'NO_WORKER_PHONE') {
          setMessages(prev => [...prev, { id: 'call-err-' + Date.now(), role: 'assistant', content: '⚠️ Your profile has no phone number. Add your cell in Profile Settings to make calls.', created_at: new Date().toISOString() }]);
        } else {
          setMessages(prev => [...prev, { id: 'call-err-' + Date.now(), role: 'assistant', content: '❌ ' + (data.error || 'Failed to start call'), created_at: new Date().toISOString() }]);
        }
      } else {
        setMessages(prev => [...prev, { id: 'call-ok-' + Date.now(), role: 'assistant', content: '📞 Your phone (' + data.worker_phone + ') is ringing — answer to connect to ' + callState.recipientName + '.', created_at: new Date().toISOString() }]);
      }
    } catch (err) {
      console.error('[InitiateCall]', err);
    } finally {
      setCallDialing(false);
      setCallState(null);
    }
  };
  const [addContactPrompt, setAddContactPrompt] = useState(null); // { name }

  const handleShareCase = async (record) => {
    const activeRecord = record || selectedRecord;
    if (!shareCaseState || !activeRecord) return;
    try {
      const token = localStorage.getItem('lf_token');
      const res = await fetch('/api/chat-ai/share-case', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ recordId: activeRecord.record_id, recipientName: shareCaseState.recipient_full_name || shareCaseState.recipient_name, recipientType: shareCaseState.recipient_type || 'user', recipientEmail: shareCaseState.recipient_email, customMessage: shareCaseState.custom_message || null }),
      });
      const data = await res.json();
      if (!res.ok) {
        console.error('[ShareCase] API error:', data.error);
        setMessages(prev => [...prev, {
          id: 'share-err-' + Date.now(), role: 'assistant',
          content: '❌ Could not send: ' + (data.error || 'Unknown error'),
          created_at: new Date().toISOString(),
        }]);
        setShareCaseState(null);
        return;
      }
      if (data.multiple) {
        setShareCaseState(prev => ({ ...prev, multiple_matches: data.matches }));
        return;
      }
      setShareCaseState(null);
      setMessages(prev => [...prev, {
        id: 'share-confirm-' + Date.now(), role: 'assistant',
        content: '✅ Case link sent to ' + data.sent_to + ' (' + data.email + ').',
        created_at: new Date().toISOString(),
      }]);
    } catch (err) {
      console.error('[ShareCase]', err);
    }
  };

  const [transitionNotes, setTransitionNotes] = useState({});
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docLabels, setDocLabels] = useState({});
  const [selectedDocCategories, setSelectedDocCategories] = useState({});
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyOffset, setHistoryOffset] = useState(0);
  const [hasMoreHistory, setHasMoreHistory] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const fileInputRef = useRef(null);
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);
  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  const speechSupported = !!SpeechRecognition;
  const [pendingCreate, setPendingCreate] = useState(null);
  const [createLocked, setCreateLocked] = useState(false);
  const [duplicateState, setDuplicateState] = useState(null);
  const [pendingIntakeCreate, setPendingIntakeCreate] = useState(null);
  const [isPinning, setIsPinning] = useState(false);
  const [toast, setToast] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Load history when record selected
  useEffect(() => {
    setMessages([]);
    setConversationId(null);
    setPendingCreate(null);
    setShowSearch(false);
    setSearchResults([]);
    setHistoryOffset(0);
    setHasMoreHistory(false);
    setSending(false);
    if (selectedRecord?.record_id) {
      setLoadingHistory(true);
      getHistory(selectedRecord.record_id)
        .then(data => {
          if (data.messages?.length > 0) {
            const loaded = data.messages.filter(m => m.role !== 'system').map((m, i) => ({
              id: m.id || 'h-' + i,
              role: m.role,
              content: m.content,
              created_at: m.created_at,
              fromHistory: true,
              user_name: m.role === 'user' 
                ? (m.first_name ? `${m.first_name} ${m.last_name || ''}`.trim() : currentUserName)
                : 'Sixty10 AI',
            }));
            setMessages(loaded);
          }
          if (data.active_conversation_id) setConversationId(data.active_conversation_id);
          setHasMoreHistory(data.messages?.length >= 300);
          setHistoryOffset(data.messages?.length || 0);
        })
        .catch(() => {})
        .finally(() => setLoadingHistory(false));
    }
  }, [selectedRecord?.record_id]);

  // Listen for new intake event from LeftPanel
  useEffect(() => {
    const handler = () => {
      setMessages([]);
      setConversationId(null);
      setPendingCreate(null);
      setTimeout(() => {
        const welcomeMsg = {
          id: 'intake-welcome',
          role: 'assistant',
          content: "Let's create a new intake. Tell me about the client — their name, what happened, and any other details you have. I'll capture everything.",
          created_at: new Date().toISOString(),
        };
        setMessages([welcomeMsg]);
      }, 100);
    };
    window.addEventListener('startIntake', handler);
    return () => window.removeEventListener('startIntake', handler);
  }, []);

  // Cleanup speech recognition on unmount
  useEffect(() => {
    return () => { recognitionRef.current?.stop(); };
  }, []);

  const toggleVoice = () => {
    if (!speechSupported) return;
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    let finalTranscript = '';
    recognition.onstart = () => setIsListening(true);
    recognition.onresult = (event) => {
      let interimTranscript = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript + ' ';
        } else {
          interimTranscript += transcript;
        }
      }
      setInput(finalTranscript + interimTranscript);
    };
    recognition.onerror = (event) => {
      console.error('[Voice] Speech recognition error:', event.error);
      setIsListening(false);
    };
    recognition.onend = () => {
      setIsListening(false);
      setInput(prev => prev.trim());
    };
    recognitionRef.current = recognition;
    recognition.start();
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleDeleteDocument = async (msg) => {
    if (!msg.document?.file_id) return;
    try {
      await deleteDocument(msg.document.file_id);
      setMessages(prev => prev.filter(m => m.id !== msg.id));
    } catch(e) { console.error('[DeleteDoc] error:', e); }
  };

  const loadMoreHistory = async () => {
    if (!selectedRecord?.record_id || loadingMore || !hasMoreHistory) return;
    setLoadingMore(true);
    try {
      const data = await getHistory(selectedRecord.record_id, { limit: 200, offset: historyOffset });
      if (data.messages?.length > 0) {
        const older = data.messages
          .filter(m => m.role !== 'system')
          .map((m, i) => ({
            id: m.id || 'older-' + i,
            role: m.role,
            content: m.content,
            created_at: m.created_at,
            fromHistory: true,
            user_name: m.role === 'user' ? currentUserName : 'Sixty10 AI',
          }));
        setMessages(prev => [...older, ...prev]);
        setHistoryOffset(prev => prev + data.messages.length);
        setHasMoreHistory(data.messages.length >= 200);
      } else {
        setHasMoreHistory(false);
      }
    } catch(e) { console.error('loadMore error:', e); }
    finally { setLoadingMore(false); }
  };

  const handleSendSilent = async (text) => {
    // Send message to AI without showing it as a user message in the UI
    if (!text || sending) return;
    setSending(true);
    try {
      const history = messages.slice(-8).map(m => ({ role: m.role, content: m.content }));
      const result = await sendMessage({
        message: text,
        conversationId,
        recordId: selectedRecord?.record_id || null,
        history,
      });
      if (result.conversationId) setConversationId(result.conversationId);
      const resolvedRecordId = result.record_id || selectedRecord?.record_id;
      const enrichedAction = result.action ? { ...result.action, record_id: resolvedRecordId } : null;
      const aiMsg = {
        id: 'a-' + Date.now(), role: 'assistant',
        content: result.reply, intent: result.intent,
        action: enrichedAction, record_id: resolvedRecordId,
        created_at: new Date().toISOString(),
      };
      setMessages(prev => [...prev, aiMsg]);
      if (result.intent === 'CREATE_RECORD' && result.action) {
        setPendingCreate({ ...result.action, record_id: result.record_id });
      }
      if (result.intent === 'CREATE_INTAKE' && result.action) {
        setPendingIntakeCreate({
          contact_fields: result.action.contact_fields || {},
          intake_fields: result.action.intake_fields || {},
          existing_contact_id: result.action.existing_contact_id || null,
        });
      }
      if (result.intent === 'INTAKE_DUPLICATE' && result.action) {
        setDuplicateState({
          mode: 'intake',
          level: result.action.duplicate_level,
          matches: result.action.duplicate_matches || [],
          overrideNote: '',
          showOverrideInput: false,
          contact_fields: result.action.contact_fields || {},
          intake_fields: result.action.intake_fields || {},
        });
      }
      if (result.intent === 'RECORD_DUPLICATE' && result.action) {
        setDuplicateState({
          mode: 'record',
          level: result.action.duplicate_level,
          matches: result.action.duplicate_matches || [],
          overrideNote: '',
          showOverrideInput: false,
          record_type_slug: result.action.record_type_slug,
          record_type_name: result.action.record_type_name,
          field_updates: result.action.field_updates || [],
        });
        setPendingCreate(result.action);
      }
      // Queue additional tasks if AI returned multiple
      // SHARE_CASE handled in Block2 with validation
      if (result.intent === 'CREATE_TASK' && result.action?.additional_tasks?.length > 0) {
        setTaskQueue(result.action.additional_tasks);
      }
      // Handle PANEL_COMMAND in silent sends too
      if (result.intent === 'PANEL_COMMAND' && result.action) {
        const { command, record_id, sort_by, type_filter, urgency_filter, search_query } = result.action;
        if (command === "navigate" && record_id) { console.log("[ChatPanel] dispatching panelNavigate", record_id); /* fired below */ }
        if (command === 'sort' && sort_by) window.dispatchEvent(new CustomEvent('panelSort', { detail: { sort_by } }));
        if (command === 'filter') window.dispatchEvent(new CustomEvent('panelFilter', { detail: { type_filter, urgency_filter } }));
        if (command === 'search' && search_query) window.dispatchEvent(new CustomEvent('panelSearch', { detail: { search_query } }));
      }
    } catch(err) {
      setMessages(prev => [...prev, {
        id: 'err-' + Date.now(), role: 'assistant',
        content: 'Something went wrong analyzing the document.',
        created_at: new Date().toISOString(),
      }]);
    } finally { setSending(false); }
  };

  const handleSend = async (text) => {
    let content = (text || input).trim();
    if (!content || sending) return;

    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
    }

    const userMsg = { id: 'u-' + Date.now(), role: 'user', content, created_at: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setSending(true);

    try {
      const history = messages.slice(-8).map(m => ({ role: m.role, content: m.content }));
      const result = await sendMessage({
        message: content,
        conversationId,
        recordId: selectedRecord?.record_id || null,
        history,
      });

      console.log("[handleSend] result intent:", result.intent, "action:", JSON.stringify(result.action));
      if (result.conversationId) setConversationId(result.conversationId);

      // Attach record_id to action so confirm button always has it
      const resolvedRecordId = result.record_id || selectedRecord?.record_id;
      const enrichedAction = result.action ? {
        ...result.action,
        record_id: resolvedRecordId,
      } : null;

      const aiMsg = {
        id: 'a-' + Date.now(),
        role: 'assistant',
        content: result.reply,
        intent: result.intent,
        action: enrichedAction,
        record_id: resolvedRecordId,
        created_at: new Date().toISOString(),
      };
      setMessages(prev => [...prev, aiMsg]);

      // If CREATE_RECORD intent, store pending create for confirmation
      if (result.intent === 'CREATE_RECORD' && result.action) {
        setPendingCreate({ ...result.action, record_id: result.record_id });
      }
      if (result.intent === 'CREATE_INTAKE' && result.action) {
        setPendingIntakeCreate({
          contact_fields: result.action.contact_fields || {},
          intake_fields: result.action.intake_fields || {},
          existing_contact_id: result.action.existing_contact_id || null,
        });
      }
      if (result.intent === 'INTAKE_DUPLICATE' && result.action) {
        setDuplicateState({
          mode: 'intake',
          level: result.action.duplicate_level,
          matches: result.action.duplicate_matches || [],
          overrideNote: '',
          showOverrideInput: false,
          contact_fields: result.action.contact_fields || {},
          intake_fields: result.action.intake_fields || {},
        });
      }
      if (result.intent === 'RECORD_DUPLICATE' && result.action) {
        setDuplicateState({
          mode: 'record',
          level: result.action.duplicate_level,
          matches: result.action.duplicate_matches || [],
          overrideNote: '',
          showOverrideInput: false,
          record_type_slug: result.action.record_type_slug,
          record_type_name: result.action.record_type_name,
          field_updates: result.action.field_updates || [],
        });
        setPendingCreate(result.action);
      }
      // Handle PANEL_COMMAND — dispatch window events for LeftPanel
            if (result.intent === 'SHARE_CASE' && result.action) {
        console.log('[SHARE_CASE] Block2 setting state:', result.action.recipient_name);
        // Validate recipient exists before showing card
        (async () => {
          try {
            const token = localStorage.getItem('lf_token');
            const validateRes = await fetch('/api/chat-ai/validate-recipient', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({ recipientName: result.action.recipient_name }),
            });
            const validateData = await validateRes.json();
            if (validateData.not_found) {
              // Not found in users or contacts — offer to add as contact
              setMessages(prev => {
                const msgs = [...prev];
                const lastIdx = msgs.map(m => m.role).lastIndexOf('assistant');
                if (lastIdx !== -1) msgs[lastIdx] = {
                  ...msgs[lastIdx],
                  content: 'I couldn\'t find "' + result.action.recipient_name + '" in your contacts or team. Would you like me to add them as a contact first?',
                };
                return msgs;
              });
              setAddContactPrompt({ name: result.action.recipient_name });
            } else if (!validateRes.ok || validateData.error) {
              setMessages(prev => {
                const msgs = [...prev];
                const lastIdx = msgs.map(m => m.role).lastIndexOf('assistant');
                if (lastIdx !== -1) msgs[lastIdx] = {
                  ...msgs[lastIdx],
                  content: '❌ ' + (validateData.error || 'User not found: ' + result.action.recipient_name),
                };
                return msgs;
              });
            } else if (validateData.multiple) {
              // Multiple matches (could be mix of users and contacts)
              setShareCaseState({
                recipient_name: result.action.recipient_name,
                custom_message: result.action.custom_message || null,
                multiple_matches: validateData.matches,
                pending: true
              });
            } else {
              setShareCaseState({
                recipient_name: result.action.recipient_name,
                recipient_email: validateData.email,
                recipient_full_name: validateData.full_name,
                recipient_type: validateData.type || 'user',
                custom_message: result.action.custom_message || null,
                pending: true
              });
            }
          } catch(err) {
            console.error('[SHARE_CASE validate]', err);
          }
        })();
      }
      if (result.intent === 'SEND_SMS' && result.action) {
        const smsPhones = result.action.phones || [];
        if (result.action.message || result.action.phones) {
          setSmsState({
            recordId: result.record_id || result.action.record_id || null,
            phone: smsPhones.length === 1 ? smsPhones[0].number : (result.action.phone || null),
            phones: smsPhones.length > 1 ? smsPhones : null,
            selectedPhone: smsPhones.length === 1 ? 0 : null,
            message: result.action.message,
            recipientName: result.action.recipient_name,
          });
        }
      }
      if (result.intent === 'INITIATE_CALL' && result.action) {
        const phones = result.action.phones || [];
        setCallState({
          recordId: result.record_id || result.action.record_id || null,
          phone: phones.length === 1 ? phones[0].number : (result.action.phone || null),
          phones: phones.length > 1 ? phones : null,
          selectedPhone: phones.length === 1 ? 0 : null,
          recipientName: result.action.recipient_name,
        });
      }
      if (result.intent === 'DISAMBIGUATE' && result.action?.options) {
        setDisambiguateState({
          context: result.action.context,
          originalRequest: result.action.original_request,
          savedMessage: result.action.message || null,
          options: result.action.options,
          reply: result.reply,
        });
      }
      if (result.intent === 'ADD_PARTY' && result.action) {
        setAddPartyState({
          recordId: result.record_id || selectedRecord?.record_id,
          contactId: result.action.contact_id || null,
          contactName: result.action.party_name || result.action.contact_name,
          role: result.action.party_role || result.action.role,
          notFound: result.action.not_found || false,
          candidates: result.action.candidates || null,
        });
      }
      if (result.intent === 'MANAGE_TRIGGERS' && result.action) {
        setTriggerState({
          field_name: result.action.field_name,
          operator: result.action.operator || 'any_change',
          trigger_value: result.action.trigger_value || null,
          action_type: result.action.action_type || 'create_task',
          task_title: result.action.task_title,
          task_description: result.action.task_description || null,
          priority: result.action.priority || 'normal',
          due_days_offset: result.action.due_days_offset || 0,
          assign_to_name: result.action.assign_to_name || null,
          notify_whole_team: result.action.notify_whole_team || false,
          notify_name: result.action.notify_name || null,
          label: result.action.label || null,
        });
      }
      if (result.intent === 'DRAFT_DEMAND_LETTER') {
        setDemandLetterState(result);
      }
      if (result.intent === 'REMOVE_PARTY' && result.action) {
        setRemovePartyState({
          recordId: result.record_id || selectedRecord?.record_id,
          contactName: result.action.party_name || result.action.contact_name,
          role: result.action.party_role || result.action.role,
        });
      }
      if (result.intent === 'PANEL_COMMAND' && result.action) {
        const { command, record_id, sort_by, type_filter, urgency_filter, search_query } = result.action;
        if (command === 'navigate' && record_id) {
          console.log("[ChatPanel] dispatching panelNavigate2", record_id);
          setTimeout(() => window.dispatchEvent(new CustomEvent('panelNavigate', { detail: { record_id } })), 300);
        }
        if (command === 'sort' && sort_by) {
          window.dispatchEvent(new CustomEvent('panelSort', { detail: { sort_by } }));
        }
        if (command === 'filter') {
          window.dispatchEvent(new CustomEvent('panelFilter', { detail: { type_filter, urgency_filter } }));
        }
        if (command === 'search' && search_query) {
          window.dispatchEvent(new CustomEvent('panelSearch', { detail: { search_query } }));
        }
      }
      // panelNavigate fired in PANEL_COMMAND handler above — no duplicate needed
    } catch (err) {
      setMessages(prev => [...prev, {
        id: 'err-' + Date.now(), role: 'assistant',
        content: 'Something went wrong. Please try again.',
        created_at: new Date().toISOString(),
      }]);
    } finally {
      setSending(false);
    }
  };

  const handleCreateRecord = async (overrideDuplicate = false, overrideNote = '') => {
    if (!pendingCreate || createLocked) return;
    setCreateLocked(true);
    setExecutingAction('create');
    try {
      const result = await createRecord({
        record_type_slug: pendingCreate.record_type_slug,
        field_updates: pendingCreate.field_updates || [],
        extended_data: pendingCreate.extended_data || {},
        confirmed_no_duplicate: overrideDuplicate,
        override_duplicate: overrideDuplicate,
        override_note: overrideNote || undefined,
      });
      setDuplicateState(null);
      const pendingIntake = pendingCreate?.extended_data?.pending_intake;
      // Only clear pendingCreate if this is NOT a Contact with pending intake
      if (!(result.record_type_slug === 'contact' && pendingIntake)) {
        setPendingCreate(null);
      }
      const successMsg = {
        id: 'created-' + Date.now(),
        role: 'system',
        isSuccess: true,
        content: `✓ ${result.record_type_name} created: ${result.title}${result.reference_number ? ' (' + result.reference_number + ')' : ''}`,
        record: result,
        created_at: new Date().toISOString(),
      };
      setMessages(prev => [...prev, successMsg]);

      // If this was a Contact creation with a pending Intake, auto-create the Intake next
      if (result.record_type_slug === 'contact' && pendingIntake) {
        const intakeFieldUpdates = Object.entries(pendingIntake)
          .map(([field_name, value]) => ({ field_name, value }));
        intakeFieldUpdates.push({ field_name: 'client', value: result.record_id });

        setPendingCreate({
          record_type_slug: 'intake',
          record_type_name: 'Intake',
          field_updates: intakeFieldUpdates,
          extended_data: {},
          narrative: `Create Intake linked to ${result.title}`,
          follow_up_questions: [],
        });

        setMessages(prev => [...prev, {
          id: 'intake-next-' + Date.now(),
          role: 'assistant',
          content: `✓ Contact created for ${result.title}. Now let's create the linked Intake — confirm below.`,
          intent: 'CREATE_RECORD',
          created_at: new Date().toISOString(),
        }]);
      } else {
        setPendingCreate(null);
        if (result.record_type_slug !== 'contact') {
          onSelectRecord({
            record_id: result.record_id,
            title: result.title,
            record_type_name: result.record_type_name,
            urgency: 'green',
          });
        }
      }
    } catch (err) {
      const errData = err?.response?.data;
      if (errData?.error === 'duplicate_detected' || errData?.error === 'possible_duplicate') {
        setDuplicateState({
          mode: 'record',
          level: errData.duplicate_level || errData.level,
          matches: errData.duplicate_matches || errData.matches || [],
          overrideNote: '',
          showOverrideInput: false,
          record_type_slug: pendingCreate?.record_type_slug,
          record_type_name: pendingCreate?.record_type_name,
          field_updates: pendingCreate?.field_updates || [],
          extended_data: pendingCreate?.extended_data || {},
        });
      } else {
        setMessages(prev => [...prev, {
          id: 'err-' + Date.now(), role: 'assistant',
          content: errData?.message || 'Failed to create record. Please try again.',
          created_at: new Date().toISOString(),
        }]);
      }
    } finally {
      setExecutingAction(null);
      setCreateLocked(false);
    }
  };

  const handleCreateIntake = async () => {
    if (!pendingIntakeCreate || createLocked) return;
    setCreateLocked(true);
    setExecutingAction('create');
    try {
      const result = await createIntake(pendingIntakeCreate);
      setPendingIntakeCreate(null);
      setMessages(prev => [...prev, {
        id: 'intake-created-' + Date.now(),
        role: 'system', isSuccess: true,
        content: `✓ Contact and Intake created: ${result.intake_title}${result.intake_reference ? ' (' + result.intake_reference + ')' : ''}`,
        created_at: new Date().toISOString(),
      }]);
      if (onRefreshList) onRefreshList();
      setTimeout(() => { if (onRefreshProfile) onRefreshProfile(); }, 500);
    } catch(err) {
      const errData = err?.response?.data;
      if (errData?.error === 'duplicate_detected' || errData?.error === 'possible_duplicate' || errData?.error === 'duplicate_intake') {
        setDuplicateState({
          mode: 'intake',
          level: errData.duplicate_level || errData.level,
          matches: errData.duplicate_matches || errData.matches || [],
          overrideNote: '',
          showOverrideInput: false,
          contact_fields: errData.contact_fields || pendingIntakeCreate?.contact_fields || {},
          intake_fields: errData.intake_fields || pendingIntakeCreate?.intake_fields || {},
          existing_contact_id: errData.existing_contact_id || null,
        });
        setPendingIntakeCreate(null);
      } else {
        setMessages(prev => [...prev, {
          id: 'err-' + Date.now(), role: 'assistant',
          content: errData?.message || 'Failed to create intake. Please try again.',
          created_at: new Date().toISOString(),
        }]);
      }
    } finally {
      setExecutingAction(null);
      setCreateLocked(false);
    }
  };

  // Unified duplicate handlers
  const handleDupUseExisting = async () => {
    if (!duplicateState) return;
    if (duplicateState.mode === 'record') {
      // Dismiss card, navigate to existing record
      const match = duplicateState.matches[0];
      setDuplicateState(null);
      setPendingCreate(null);
      setMessages(prev => [...prev, {
        id: 'dup-use-' + Date.now(), role: 'system', isSuccess: true,
        content: `Got it — no new record created.`,
        created_at: new Date().toISOString(),
      }]);
      if (match?.record_id) {
        window.dispatchEvent(new CustomEvent('panelNavigate', { detail: { record_id: match.record_id } }));
      }
    } else {
      // Intake mode — create intake linked to existing contact
      setCreateLocked(true);
      setExecutingAction('create');
      try {
        const result = await createIntake({
          contact_fields: {},
          intake_fields: duplicateState.intake_fields,
          existing_contact_id: duplicateState.matches[0].record_id,
          override_duplicate: true,
        });
        setDuplicateState(null);
        setMessages(prev => [...prev, {
          id: 'intake-' + Date.now(), role: 'system', isSuccess: true,
          content: `✓ Intake created: ${result.intake_title}${result.intake_reference ? ' (' + result.intake_reference + ')' : ''} — linked to existing Contact.`,
          created_at: new Date().toISOString(),
        }]);
        if (onRefreshList) onRefreshList();
      setTimeout(() => { if (onRefreshProfile) onRefreshProfile(); }, 500);
      } catch(err) {
        setMessages(prev => [...prev, { id: 'err-' + Date.now(), role: 'assistant', content: 'Failed to create intake.', created_at: new Date().toISOString() }]);
      } finally { setExecutingAction(null); setCreateLocked(false); }
    }
  };

  const handleDupCreateAnyway = async (overrideNote = '') => {
    if (!duplicateState) return;
    setCreateLocked(true);
    setExecutingAction('create');
    try {
      if (duplicateState.mode === 'record') {
        const result = await createRecord({
          record_type_slug: duplicateState.record_type_slug,
          field_updates: duplicateState.field_updates || [],
          extended_data: duplicateState.extended_data || {},
          override_duplicate: true,
          override_note: overrideNote,
        });
        setDuplicateState(null);
        setPendingCreate(null);
        setMessages(prev => [...prev, {
          id: 'created-' + Date.now(), role: 'system', isSuccess: true,
          content: `✓ ${result.record_type_name} created: ${result.title}${result.reference_number ? ' (' + result.reference_number + ')' : ''}`,
          created_at: new Date().toISOString(),
        }]);
      } else {
        const result = await createIntake({
          contact_fields: duplicateState.contact_fields || {},
          intake_fields: duplicateState.intake_fields || {},
          existing_contact_id: duplicateState.existing_contact_id || null,
          confirmed_no_duplicate: true,
          override_duplicate: true,
          override_note: overrideNote,
        });
        setDuplicateState(null);
        setMessages(prev => [...prev, {
          id: 'intake-' + Date.now(), role: 'system', isSuccess: true,
          content: `✓ New Contact + Intake created: ${result.intake_title}${result.intake_reference ? ' (' + result.intake_reference + ')' : ''}`,
          created_at: new Date().toISOString(),
        }]);
        if (onRefreshList) onRefreshList();
      setTimeout(() => { if (onRefreshProfile) onRefreshProfile(); }, 500);
      }
    } catch(err) {
      setMessages(prev => [...prev, { id: 'err-' + Date.now(), role: 'assistant', content: 'Failed to create record.', created_at: new Date().toISOString() }]);
    } finally { setExecutingAction(null); setCreateLocked(false); }
  };

  const handleUpdateRecord = async (msg) => {
    if (!msg.action || executingUpdate) return;
    setExecutingUpdate(true);
    try {
      // Get record_id from message, action, or selected record
      const recordId = msg.action?.record_id || msg.record_id || selectedRecord?.record_id;
      console.log('[UpdateRecord] using record_id:', recordId);
      if (!recordId) {
        console.error('[UpdateRecord] No record_id — cannot update');
        return;
      }
      const result = await updateRecord({
        record_id: recordId,
        field_updates: msg.action.field_updates || [],
        extended_data: msg.action.extended_data || {},
      });
      const fieldNames = (msg.action.field_updates || []).map(f => `${f.field_name} = ${Array.isArray(f.value) ? f.value.join(', ') : f.value}`).join(', ');
      await logActionToHistory('confirmed', `Updated fields on ${selectedRecord?.title || 'record'}: ${fieldNames}`);
      setTimeout(() => { if (onRefreshProfile) onRefreshProfile(); }, 500);
      const successMsg = {
        id: 'updated-' + Date.now(),
        role: 'system',
        isSuccess: true,
        content: `✓ Updated ${result.field_count || 0} field${result.field_count !== 1 ? 's' : ''} on ${selectedRecord?.title || 'record'}.`,
        created_at: new Date().toISOString(),
      };
      setMessages(prev => prev.map(m => m.id === msg.id
        ? { ...m, action: { ...m.action, confirmed: true } }
        : m
      ).concat([successMsg]));
    } catch (err) {
      console.error('updateRecord error:', err);
    } finally {
      setExecutingUpdate(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim() || !selectedRecord?.record_id) return;
    setSearching(true);
    try {
      const data = await searchHistory(selectedRecord.record_id, searchQuery.trim());
      setSearchResults(data.results || []);
    } catch(e) { console.error('[Search] error:', e); }
    finally { setSearching(false); }
  };

  const logActionToHistory = async (status, summary) => {
    if (!conversationId) return;
    try {
      await logAction({ conversation_id: conversationId, status, summary });
    } catch(e) { console.error('[LogAction] error:', e); }
  };

  const handleTransition = async (msg) => {
    if (!msg.action || executingTransition) return;
    setExecutingTransition(true);
    try {
      const recordId = msg.action.record_id || msg.record_id || selectedRecord?.record_id;
      if (!recordId) { console.error('[Transition] No record_id'); return; }
      const noteText = transitionNotes[msg.id] || msg.action.note || '';
      const result = await executeTransition({
        record_id: recordId,
        transition_label: msg.action.transition_label,
        to_stage_name: msg.action.to_stage_name,
        note: noteText || undefined,
        conversation_id: conversationId,
      });
      const successMsg = { id: 'trans-'+Date.now(), role: 'system', isSuccess: true, content: `✓ Moved to ${result.to_stage} stage.`, created_at: new Date().toISOString() };
      await logActionToHistory('confirmed', `Moved ${selectedRecord?.title || 'record'} from ${result.from_stage} to ${result.to_stage} stage`);
      setTimeout(() => { if (onRefreshProfile) onRefreshProfile(); }, 500);
      setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, action: { ...m.action, confirmed: true } } : m).concat([successMsg]));
      if (onSelectRecord && selectedRecord) onSelectRecord({ ...selectedRecord, stage_name: result.to_stage });
    } catch(err) {
      console.error('[Transition] error:', err);
      setMessages(prev => [...prev, { id: 'err-'+Date.now(), role: 'assistant', content: err?.response?.data?.error || 'Transition failed. Please try again.', created_at: new Date().toISOString() }]);
    } finally { setExecutingTransition(false); }
  };

  const dismissTransition = (msg) => {
    logActionToHistory('cancelled', `Cancelled stage transition to ${msg.action?.to_stage_name || 'next stage'}`);
    setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, action: { ...m.action, confirmed: true } } : m));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedRecord?.record_id) return;
    e.target.value = '';
    setUploadingDoc(true);

    // Show uploading message
    const uploadingMsg = {
      id: 'uploading-' + Date.now(), role: 'system', isUploading: true,
      content: `Uploading and analyzing ${file.name}...`, created_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, uploadingMsg]);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('record_id', selectedRecord.record_id);
      const result = await uploadDocument(formData);

      // Replace uploading message with document card
      const docMsg = {
        id: 'doc-' + Date.now(), role: 'assistant', intent: 'DOCUMENT_UPLOAD',
        content: result.ai_summary || `I've received **${file.name}**. No text could be extracted for summarization.`,
        document: result, created_at: new Date().toISOString(),
      };
      setMessages(prev => prev.filter(m => m.id !== uploadingMsg.id).concat([docMsg]));
    } catch(err) {
      setMessages(prev => prev.filter(m => m.id !== uploadingMsg.id).concat([{
        id: 'err-' + Date.now(), role: 'assistant',
        content: 'Failed to upload document. Please try again.',
        created_at: new Date().toISOString(),
      }]));
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleSaveDocLabel = async (msg) => {
    if (!msg.document?.file_id) return;
    const label = docLabels[msg.id]?.trim() || '';
    const selected = selectedDocCategories[msg.id] || [];
    if (selected.length === 0 && !label) return;
    try {
      const result = await saveDocumentTags({
        file_id: msg.document.file_id,
        selected_categories: selected,
        custom_tags: label,
      });
      setMessages(prev => prev.map(m => m.id === msg.id
        ? { ...m, document: { ...m.document,
            confirmed_category: result.primary_category || selected[0] || 'other',
            saved_tags: result.tags,
            saved_label: label || null,
          }}
        : m
      ));
      setDocLabels(prev => ({ ...prev, [msg.id]: '' }));
      setSelectedDocCategories(prev => ({ ...prev, [msg.id]: [] }));

      // Auto-select the suggested category
      if (result.suggested_category) {
        setSelectedDocCategories(prev => ({
          ...prev,
          [docMsg.id]: [result.suggested_category]
        }));
      }

      // Refresh left panel profile to update doc count
      if (onSelectRecord && selectedRecord) {
        onSelectRecord({ ...selectedRecord });
      }

      // Auto-trigger AI analysis of the uploaded document (silent — not shown as user message)
      if (msg.document?.ai_summary && selectedRecord?.record_id) {
        const autoPrompt = `I just uploaded a document: "${msg.document.filename}" categorized as ${result.tags?.join(', ') || result.primary_category}. AI summary: "${msg.document.ai_summary}". Based on this document, what fields on this record can you update? Please propose any field updates you can extract from this document.`;
        setTimeout(() => handleSendSilent(autoPrompt), 500);
      }
    } catch(e) { console.error('[SaveTags] error:', e); }
  };

  const handleToggleDocCategory = (msgId, category) => {
    setSelectedDocCategories(prev => {
      const current = prev[msgId] || [];
      const exists = current.includes(category);
      return {
        ...prev,
        [msgId]: exists ? current.filter(c => c !== category) : [...current, category]
      };
    });
  };

  const handleConfirmCategory = async (msg, category) => {
    handleToggleDocCategory(msg.id, category);
  };

  const handleUpdateTask = async (msg) => {
    console.log('[UpdateTask] action:', JSON.stringify(msg.action));
    if (!msg.action || executingTask) return;
    setExecutingTask(true);
    try {
      const recordId = msg.action.record_id || msg.record_id || selectedRecord?.record_id;
      const result = await updateTask({
        record_id: recordId,
        task_search: msg.action.task_search,
        due_date_text: msg.action.due_date_text || msg.action.due_date,
        priority: msg.action.priority,
        status: msg.action.status,
        title: msg.action.title,
        notes: msg.action.notes || null,
        conversation_id: conversationId,
      });
      await logActionToHistory('confirmed', `Updated task "${result.task.title}"${msg.action.due_date ? ' — due date set to ' + new Date(msg.action.due_date).toLocaleDateString() : ''}`);
      setTimeout(() => { if (onRefreshProfile) onRefreshProfile(); }, 500);
      const successMsg = {
        id: 'task-upd-' + Date.now(), role: 'system', isSuccess: true,
        content: `✓ Task updated: "${result.task.title}"${result.task.due_date ? ' — due ' + new Date(result.task.due_date).toLocaleDateString() : ''}`,
        created_at: new Date().toISOString(),
      };
      setMessages(prev => prev.map(m => m.id === msg.id
        ? { ...m, action: { ...m.action, confirmed: true } }
        : m
      ).concat([successMsg]));
      if (onSelectRecord && selectedRecord) onSelectRecord({ ...selectedRecord });
    } catch(err) {
      console.error('[UpdateTask] error:', err);
      setMessages(prev => [...prev, {
        id: 'err-' + Date.now(), role: 'assistant',
        content: err?.response?.data?.error || 'Failed to update task.',
        created_at: new Date().toISOString(),
      }]);
    } finally { setExecutingTask(false); }
  };

  const handleCreateTask = async (msg) => {
    if (!msg.action || executingTask) return;
    setExecutingTask(true);
    try {
      const recordId = msg.action.record_id || msg.record_id || selectedRecord?.record_id;
      const result = await createTask({
        record_id: recordId,
        title: msg.action.title,
        description: msg.action.description || null,
        priority: msg.action.priority || 'normal',
        due_date: msg.action.due_date || null,
        conversation_id: conversationId,
      });
      await logActionToHistory('confirmed', `Task created: "${result.task.title}" — ${result.task.priority} priority${result.task.due_date ? ', due ' + new Date(result.task.due_date).toLocaleDateString() : ''}`);
      setTimeout(() => { if (onRefreshProfile) onRefreshProfile(); }, 500);
      const successMsg = {
        id: 'task-' + Date.now(), role: 'system', isSuccess: true,
        content: `✓ Task created: "${result.task.title}"${result.task.due_date ? ' — due ' + new Date(result.task.due_date).toLocaleDateString() : ''}`,
        created_at: new Date().toISOString(),
      };
      setMessages(prev => prev.map(m => m.id === msg.id
        ? { ...m, action: { ...m.action, confirmed: true } }
        : m
      ).concat([successMsg]));
      // Show next task in queue if any
      setTaskQueue(prev => {
        if (prev.length === 0) return prev;
        const [next, ...rest] = prev;
        const nextMsg = {
          id: 'task-q-' + Date.now(), role: 'assistant',
          content: `Next task to create:`,
          intent: 'CREATE_TASK',
          action: { ...next, record_id: msg.action.record_id },
          created_at: new Date().toISOString(),
        };
        setTimeout(() => setMessages(m => [...m, nextMsg]), 300);
        return rest;
      });
    } catch(err) {
      console.error('[CreateTask] error:', err);
      setMessages(prev => [...prev, {
        id: 'err-' + Date.now(), role: 'assistant',
        content: 'Failed to create task. Please try again.',
        created_at: new Date().toISOString(),
      }]);
    } finally { setExecutingTask(false); }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handlePinMessage = async (content) => {
    if (isPinning || !content) return;
    setIsPinning(true);
    try {
      const res = await fetch('/api/chat-ai/pin-message', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('lf_token')}` },
        body: JSON.stringify({ messageContent: content, recordId: selectedRecord?.record_id || null }),
      });
      const data = await res.json();
      if (data.verdict === 'auto_approved') showToast('Added to knowledge base', 'success');
      else if (data.verdict === 'queued') showToast('Sent to admin for review', 'info');
      else if (data.verdict === 'blocked') showToast(data.reason || 'Cannot pin — contains PHI', 'warning');
      else showToast('Pinned', 'info');
    } catch (e) {
      showToast('Pin failed', 'error');
    } finally {
      setIsPinning(false);
    }
  };

  const prompts = selectedRecord ? CASE_PROMPTS : GLOBAL_PROMPTS;
  const placeholder = selectedRecord
    ? `Ask about ${selectedRecord.title}...`
    : 'Ask anything, create an intake, or search cases...';

  return (
<div className="h-full flex flex-col bg-gray-950 overflow-hidden">
      {/* Context header */}
      {selectedRecord && (
        <div className="px-4 py-2 border-b border-gray-800 bg-gray-900/50">
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
              selectedRecord.urgency === 'red' ? 'bg-red-500' :
              selectedRecord.urgency === 'yellow' ? 'bg-yellow-500' : 'bg-green-500'
            }`} />
            <span className="text-gray-300 text-sm font-medium flex-1 truncate">{selectedRecord.title}</span>
            {selectedRecord.record_type_name && (
              <span className="text-gray-500 text-xs flex-shrink-0">· {selectedRecord.record_type_name}</span>
            )}
            <button onClick={() => { setShowSearch(!showSearch); setSearchResults([]); setSearchQuery(''); }}
              className="text-gray-500 hover:text-gray-300 text-xs flex-shrink-0 px-2 py-1 rounded hover:bg-gray-800 transition-colors">
              🔍 Search
            </button>
          </div>
          {showSearch && (
            <div className="mt-2 flex gap-2">
              <input
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                placeholder="Search chat history..."
                className="flex-1 bg-gray-800 border border-gray-700 text-gray-200 text-xs rounded-lg px-3 py-1.5 focus:outline-none focus:border-amber-600"
              />
              <button onClick={handleSearch} disabled={searching}
                className="px-3 py-1.5 text-xs rounded-lg disabled:opacity-50" style={{ background: 'linear-gradient(135deg, #c9a84c, #a67c32)', color: '#fff8e6' }}>
                {searching ? '...' : 'Go'}
              </button>
            </div>
          )}
          {showSearch && searchResults.length > 0 && (
            <div className="mt-2 max-h-40 overflow-y-auto space-y-1">
              {searchResults.map(r => (
                <div key={r.id} className="text-xs bg-gray-800 rounded-lg px-3 py-2">
                  <p className="text-gray-400 text-[10px] mb-0.5">{new Date(r.created_at).toLocaleDateString()}</p>
                  <p className="text-gray-200 line-clamp-2">{r.content.substring(0, 150)}...</p>
                </div>
              ))}
            </div>
          )}
          {showSearch && searchResults.length === 0 && searchQuery && !searching && (
            <p className="text-xs text-gray-500 mt-1.5">No results found for "{searchQuery}"</p>
          )}
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4"
        onScroll={(e) => { if (e.target.scrollTop < 50 && hasMoreHistory && !loadingMore) loadMoreHistory(); }}>
        {loadingMore && <div className="text-center py-2"><span className="text-xs text-gray-500">Loading earlier messages...</span></div>}
        {loadingHistory && (
          <div className="flex justify-center py-8">
            <span className="text-xs text-gray-500">Loading conversation history...</span>
          </div>
        )}
        {messages.length === 0 && !loadingHistory && (
          <div className="h-full flex flex-col items-center justify-center">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ background: '#1a1710' }}>
              <Sparkles className="h-8 w-8" style={{ color: '#c9a84c' }} />
            </div>
            <h2 className="text-white text-xl font-semibold mb-1">
              {selectedRecord ? `Case: ${selectedRecord.title}` : 'How can I help?'}
            </h2>
            <p className="text-gray-500 text-sm mb-8">
              {selectedRecord ? 'Ask me anything about this case' : 'Type anything or choose a suggestion below'}
            </p>
            <div className="grid grid-cols-2 gap-2 w-full max-w-lg">
              {prompts.map((p, i) => (
                <button key={i} onClick={() => handleSend(p)}
                  className="text-left px-4 py-3 bg-gray-900 hover:bg-gray-800 border border-gray-800 hover:border-gray-700 text-gray-300 text-sm rounded-xl transition-colors">
                  {p}
                </button>
              ))}
            </div>
          </div>
        )}

        {!loadingHistory && messages.map(msg => (
          <div key={msg.id}>
            {msg.isSuccess ? (
              <div className="flex justify-center">
                <span className="text-xs px-4 py-1.5 rounded-full bg-green-900/40 text-green-400 border border-green-800/40">
                  {msg.content}
                </span>
              </div>
            ) : msg.role === 'user' ? (
              <div className="flex justify-end">
                <div className="flex flex-col items-end gap-0.5">
                  <div className="w-full px-3 py-2 rounded-2xl rounded-tr-sm text-sm" style={{ background: 'linear-gradient(135deg, #c9a84c, #a67c32)', color: '#fff8e6' }}>
                    {msg.content}
                  </div>
                  <span className="text-[10px] text-gray-600 px-1">{msg.user_name || currentUserName} · {formatMsgTime(msg.created_at)}</span>
                </div>
              </div>
            ) : (
              <div className="group flex gap-3 max-w-[85%]">
                <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: '#1a1710' }}>
                  <Sparkles className="h-3.5 w-3.5" style={{ color: '#c9a84c' }} />
                </div>
                <div className="flex-1">
                  <span className="text-[10px] px-1" style={{ color: '#c9a84c' }}>Sixty10 AI</span>
                  <div className="bg-gray-900 border border-gray-800 px-4 py-3 rounded-2xl rounded-tl-sm text-sm leading-relaxed">
                    {renderMarkdown(stripMarkdown(msg.content))}
                  </div>
                  <div className="flex items-center gap-1 mt-0.5">
                    <span className="text-[10px] text-gray-600 px-1">{formatMsgTime(msg.created_at)}</span>
                    {msg.content && !msg.fromHistory && (
                      <button
                        onClick={() => handlePinMessage(msg.content)}
                        disabled={isPinning}
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-gray-800 disabled:opacity-30"
                        title="Pin to knowledge base"
                      >
                        <Pin className="h-3 w-3" style={{ color: '#c9a84c' }} />
                      </button>
                    )}
                  </div>
                  {/* Pending create confirmation */}
                  {msg.intent === 'CREATE_RECORD' && pendingCreate && (
                    <div className="mt-2 bg-gray-900 rounded-xl p-3" style={{ border: '1px solid #3d3418' }}>
                      <p className="text-xs font-medium mb-2" style={{ color: '#c9a84c' }}>
                        Create {pendingCreate.record_type_name || 'record'} with these fields?
                      </p>
                      {(pendingCreate.field_updates || []).slice(0, 6).map((fu, i) => (
                        <div key={i} className="flex justify-between text-xs mb-1">
                          <span className="text-gray-400">{fu.field_name}</span>
                          <span className="text-gray-200">{String(fu.value)}</span>
                        </div>
                      ))}
                      {pendingCreate.follow_up_questions?.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-800">
                          <p className="text-xs text-yellow-400/70 mb-1">Still needed:</p>
                          {pendingCreate.follow_up_questions.map((q, i) => (
                            <p key={i} className="text-xs text-gray-400">• {q}</p>
                          ))}
                        </div>
                      )}
                      <div className="flex gap-2 mt-3">
                        <button onClick={() => handleCreateRecord()} disabled={executingAction === 'create'}
                          className="flex-1 py-1.5 text-xs font-semibold rounded-lg disabled:opacity-50 transition-colors"
                          style={{ background: 'linear-gradient(135deg, #c9a84c, #a67c32)', color: '#fff8e6', border: '1px solid #d4b060', borderRadius: '12px', padding: '10px 0', fontWeight: 600, fontSize: '13px', letterSpacing: '0.02em', cursor: 'pointer' }}>
                          {executingAction === 'create' ? 'Creating...' : '✓ Create Record'}
                        </button>
                        <button onClick={() => setPendingCreate(null)}
                          className="flex-1 py-1.5 text-xs rounded-lg transition-colors"
                          style={{ background: 'transparent', color: '#d94f4f', border: '1px solid #7a2424', borderRadius: '12px', padding: '10px 0', fontSize: '13px', cursor: 'pointer' }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                  {/* Duplicate Detection Card removed — now unified below message list */}
                  {/* CREATE_INTAKE confirmation card */}
                  {msg.intent === 'CREATE_INTAKE' && pendingIntakeCreate && !msg.action?.confirmed && (
                    <div className="mt-2 bg-gray-900 rounded-xl p-3" style={{ border: '1px solid #3d3418' }}>
                      <p className="text-xs font-medium mb-2" style={{ color: '#c9a84c' }}>Create Contact + Intake?</p>
                      {Object.entries(pendingIntakeCreate.contact_fields || {}).slice(0, 4).map(([k,v]) => (
                        <div key={k} className="flex justify-between text-xs mb-1">
                          <span className="text-gray-400">{k.replace(/_/g,' ')}</span>
                          <span className="text-gray-200">{Array.isArray(v) ? v.join(', ') : String(v)}</span>
                        </div>
                      ))}
                      {Object.keys(pendingIntakeCreate.intake_fields || {}).length > 0 && (
                        <div className="mt-2 pt-2" style={{ borderTop: '1px solid #3d3418' }}>
                          {Object.entries(pendingIntakeCreate.intake_fields || {}).slice(0, 3).map(([k,v]) => (
                            <div key={k} className="flex justify-between text-xs mb-1">
                              <span className="text-gray-400">{k.replace(/_/g,' ')}</span>
                              <span className="text-gray-200">{String(v)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="flex gap-2 mt-3">
                        <button onClick={handleCreateIntake} disabled={executingAction === 'create'}
                          className="flex-1 py-1.5 text-xs font-semibold rounded-lg disabled:opacity-50 transition-colors"
                          style={{ background: 'linear-gradient(135deg, #c9a84c, #a67c32)', color: '#fff8e6', border: '1px solid #d4b060', borderRadius: '12px', padding: '10px 0', fontWeight: 600, fontSize: '13px', letterSpacing: '0.02em', cursor: 'pointer' }}>
                          {executingAction === 'create' ? 'Creating...' : '✓ Create Contact + Intake'}
                        </button>
                        <button onClick={() => setPendingIntakeCreate(null)}
                          className="flex-1 py-1.5 text-xs rounded-lg transition-colors"
                          style={{ background: 'transparent', color: '#d94f4f', border: '1px solid #7a2424', borderRadius: '12px', padding: '10px 0', fontSize: '13px', cursor: 'pointer' }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                  {/* Intake Duplicate Card moved outside message list */}
                  {/* Update field confirmation */}
                  {msg.action && msg.intent === 'UPDATE_RECORD' && !msg.action.confirmed && !msg.action.dismissed && (msg.action.field_updates?.length > 0 || msg.action.extended_data) && (
                    <div className="mt-2 bg-gray-900 border border-amber-800/40 rounded-xl p-3">
                      <p className="text-xs text-amber-300 font-medium mb-2">Update these fields?</p>
                      {(msg.action.field_updates || []).slice(0, 8).map((fu, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs mb-1">
                          <span className="text-gray-400 flex-shrink-0">{fu.field_name.replace(/_/g,' ')}</span>
                          <span className="text-gray-500">→</span>
                          <span className="text-gray-200 font-medium">{Array.isArray(fu.value) ? fu.value.join(', ') : String(fu.value)}</span>
                        </div>
                      ))}
                      {msg.action.extended_data && Object.keys(msg.action.extended_data).length > 0 && (
                        <p className="text-xs text-gray-500 mt-1">+ {Object.keys(msg.action.extended_data).length} intelligence flags</p>
                      )}
                      <div className="flex gap-2 mt-3">
                        <button onClick={() => handleUpdateRecord(msg)}
                          disabled={executingUpdate}
                          className="flex-1 py-1.5 text-xs font-semibold text-white bg-green-600 hover:bg-green-700 rounded-lg disabled:opacity-50 transition-colors">
                          {executingUpdate ? 'Saving...' : '✓ Confirm Update'}
                        </button>
                        <button onClick={() => {
                            const fieldNames = (msg.action.field_updates || []).map(f => f.field_name).join(', ');
                            logActionToHistory('cancelled', `Cancelled update to: ${fieldNames} on ${selectedRecord?.title || 'record'}`);
                            setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, action: { ...m.action, dismissed: true } } : m));
                          }}
                          className="flex-1 py-1.5 text-xs text-gray-400 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors">
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                  {/* Stage transition confirmation */}
                  {msg.intent === 'TRANSITION' && msg.action && !msg.action.confirmed && (
                    <div className="mt-2 bg-gray-900 border border-blue-800/40 rounded-xl p-3">
                      <p className="text-xs text-blue-300 font-medium mb-1">Stage Transition</p>
                      <p className="text-xs text-gray-300 mb-2">Move to: <strong className="text-white">{msg.action.to_stage_name}</strong></p>
                      {msg.action.require_note && (
                        <input
                          placeholder="Add a note (required)..."
                          value={transitionNotes[msg.id] || ''}
                          onChange={e => setTransitionNotes(prev => ({ ...prev, [msg.id]: e.target.value }))}
                          className="w-full mt-1 mb-2 bg-gray-800 border border-gray-700 text-gray-200 text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-blue-600"
                        />
                      )}
                      <div className="flex gap-2 mt-2">
                        <button onClick={() => handleTransition(msg)} disabled={executingTransition || (msg.action.require_note && !(transitionNotes[msg.id]?.trim()))}
                          className="flex-1 py-1.5 text-xs font-semibold rounded-lg disabled:opacity-50 transition-colors" style={{ background: 'linear-gradient(135deg, #1a6b8a, #0d4f6b)', color: '#a8dff0', border: '1px solid #1e7a9e' }}>
                          {executingTransition ? 'Moving...' : '✓ Confirm Move'}
                        </button>
                        <button onClick={() => dismissTransition(msg)} disabled={executingTransition}
                          className="flex-1 py-1.5 text-xs text-gray-400 bg-gray-800 hover:bg-gray-700 rounded-lg disabled:opacity-50 transition-colors">
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Document upload card */}
                  {msg.intent === 'DOCUMENT_UPLOAD' && msg.document && (
                    <div className="mt-2 bg-gray-900 border border-indigo-800/40 rounded-xl p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="h-4 w-4 text-indigo-400 flex-shrink-0" />
                        <span className="text-xs font-medium text-indigo-300 truncate flex-1">{msg.document.filename}</span>
                        <span className="text-xs text-gray-500 flex-shrink-0">{msg.document.size ? (msg.document.size / 1024).toFixed(0) + ' KB' : ''}</span>
                        <button onClick={() => handleDeleteDocument(msg)}
                          className="text-[10px] flex-shrink-0 px-2.5 py-1 bg-red-900/40 hover:bg-red-800/60 text-red-400 hover:text-red-300 border border-red-800/40 rounded-lg transition-colors font-medium">
                          Cancel Upload
                        </button>
                      </div>
                      {!msg.document.confirmed_category ? (
                        <>
                          {/* Identity verification result */}
                          {msg.document.identity_verification && (
                            <div className={`mb-2 p-2 rounded-lg border text-xs ${
                              msg.document.identity_verification.name_matches_client === false
                                ? 'bg-red-950/40 border-red-700/40'
                                : 'bg-green-950/40 border-green-700/40'
                            }`}>
                              {msg.document.identity_verification.name_matches_client === false ? (
                                <p className="text-red-300 font-medium">⚠️ Name mismatch — document shows "{msg.document.identity_verification.name_on_document}". Verify this is the correct client's ID.</p>
                              ) : (
                                <p className="text-green-300 font-medium">✓ Government ID verified — {msg.document.identity_verification.name_on_document}</p>
                              )}
                              <div className="mt-1 space-y-0.5 text-gray-400">
                                {msg.document.identity_verification.dob && <p>DOB: {msg.document.identity_verification.dob}</p>}
                                {msg.document.identity_verification.expiration_date && <p>Expires: {msg.document.identity_verification.expiration_date}</p>}
                                {msg.document.identity_verification.address && <p>Address: {msg.document.identity_verification.address}</p>}
                              </div>
                            </div>
                          )}
                          <p className="text-xs text-gray-400 mb-2">
                            Suggested category: <strong className="text-indigo-300">{msg.document.suggested_category?.replace(/_/g,' ')}</strong>
                            {msg.document.confidence > 0 && <span className="text-gray-500"> ({Math.round(msg.document.confidence * 100)}% confidence)</span>}
                            {!msg.document.ocr_available && <span className="text-yellow-500 ml-1">(no text extracted — manual categorization)</span>}
                          </p>
                          <div className="flex flex-wrap gap-1.5 mb-3">
                            {(msg.document.available_categories || []).map(cat => {
                              const selected = (selectedDocCategories[msg.id] || []).includes(cat);
                              const isSuggested = cat === msg.document.suggested_category;
                              return (
                                <button key={cat}
                                  onClick={() => handleToggleDocCategory(msg.id, cat)}
                                  className={`px-2 py-1 text-[10px] rounded-full border transition-colors ${
                                    selected
                                      ? 'bg-indigo-600 border-indigo-500 text-white font-semibold'
                                      : isSuggested
                                        ? 'bg-gray-800 border-indigo-600/40 text-indigo-400/70 italic'
                                        : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-indigo-600 hover:text-indigo-300'
                                  }`}>
                                  {selected ? '✓ ' : isSuggested ? '→ ' : ''}{cat.replace(/_/g,' ')}
                                </button>
                              );
                            })}
                          </div>
                          {(selectedDocCategories[msg.id] || []).length > 0 && (
                            <p className="text-[10px] text-indigo-400 mb-2">
                              {(selectedDocCategories[msg.id] || []).length} categor{(selectedDocCategories[msg.id] || []).length === 1 ? 'y' : 'ies'} selected
                            </p>
                          )}
                          {/* Tenant custom tags */}
                          {(msg.document.custom_tags || []).length > 0 && (
                            <div className="mt-2 mb-1">
                              <p className="text-[10px] mb-1.5" style={{ color: '#a09880' }}>Your firm's tags:</p>
                              <div className="flex flex-wrap gap-1.5">
                                {(msg.document.custom_tags || []).map(tag => {
                                  const selected = (selectedDocCategories[msg.id] || []).includes(tag);
                                  return (
                                    <button key={tag}
                                      onClick={() => handleToggleDocCategory(msg.id, tag)}
                                      className="px-2 py-1 text-[10px] rounded-full transition-colors border-2"
                                      style={selected
                                        ? { background: '#c9a84c', borderColor: '#d4b060', color: '#fff8e6', fontWeight: 600 }
                                        : { background: '#1a1b1f', borderStyle: 'dashed', borderColor: '#3d3418', color: '#c9a84c' }
                                      }>
                                      {selected ? '✓ ' : '# '}{tag.replace(/_/g,' ')}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          <div className="flex gap-2 mt-2">
                            <input
                              placeholder="Add custom tags (e.g. Sofia Retainer, Jan 2026)"
                              value={docLabels[msg.id] || ''}
                              onChange={e => setDocLabels(prev => ({ ...prev, [msg.id]: e.target.value }))}
                              onKeyDown={e => { if (e.key === 'Enter' && docLabels[msg.id]?.trim()) handleSaveDocLabel(msg); }}
                              className="flex-1 bg-gray-800 border border-gray-700 text-gray-200 text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-indigo-600"
                            />
                            <button
                              onClick={() => handleSaveDocLabel(msg)}
                              disabled={!(selectedDocCategories[msg.id] || []).length && !docLabels[msg.id]?.trim()}
                              className="px-2 py-1.5 text-xs bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg disabled:opacity-40 transition-colors flex-shrink-0">
                              Save Tags
                            </button>
                          </div>
                          <p className="text-[10px] text-gray-500 mt-1">Select a category pill to confirm · Add custom tags above (optional)</p>
                        </>
                      ) : (
                        <div>
                          <p className="text-xs text-green-400">✓ Saved</p>
                          {msg.document.saved_tags?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {msg.document.saved_tags.map(tag => (
                                <span key={tag} className="px-2 py-0.5 text-[10px] bg-indigo-900/40 border border-indigo-700/40 text-indigo-300 rounded-full">
                                  {tag.replace(/_/g,' ')}
                                </span>
                              ))}
                            </div>
                          )}
                          {msg.document.ai_summary && <p className="text-xs text-gray-400 mt-1.5 italic">{msg.document.ai_summary}</p>}
                        </div>
                      )}
                    </div>
                  )}
                  {/* Task auto-execution — no confirmation needed */}
                  {msg.intent === 'CREATE_TASK' && msg.action && !msg.action.confirmed && !msg.action.dismissed && (
                    <AutoCreateTasks msg={msg} selectedRecord={selectedRecord} conversationId={conversationId}
                      conversationId={conversationId}
                      onDone={(results) => {
                        setMessages(prev => prev.map(m => m.id === msg.id
                          ? { ...m, action: { ...m.action, confirmed: true } }
                          : m
                        ).concat(results.map(r => ({
                          id: 'task-' + Date.now() + Math.random(), role: 'system', isSuccess: true,
                          content: `✓ Task created: "${r.title}"${r.due_date ? ' — due ' + r.due_date.split('T')[0] : ''}${r.recurrence_rule ? ' 🔄 ' + r.recurrence_rule : ''}`,
                          created_at: new Date().toISOString(),
                        }))));
                        // Refresh left panel to update task count
                        if (onSelectRecord && selectedRecord) onSelectRecord({ ...selectedRecord });
                      }}
                    />
                  )}
                  {/* Task update confirmation */}
                  {msg.intent === 'UPDATE_TASK' && msg.action && !msg.action.confirmed && !msg.action.dismissed && (
                    <div className="mt-2 bg-gray-900 border border-blue-800/40 rounded-xl p-3">
                      <p className="text-xs text-blue-300 font-medium mb-1">Update Task</p>
                      <p className="text-xs text-gray-400">Finding: <span className="text-white">"{msg.action.task_search}"</span></p>
                      <div className="mt-1.5 space-y-0.5 text-xs">
                        {(msg.action.due_date_text || msg.action.due_date) && <p className="text-gray-300">New due date: <strong>{msg.action.due_date_text || msg.action.due_date}</strong></p>}
                        {msg.action.priority && <p className="text-gray-300">New priority: <strong>{msg.action.priority}</strong></p>}
                        {msg.action.status && <p className="text-gray-300">New status: <strong>{msg.action.status}</strong></p>}
                        {msg.action.title && <p className="text-gray-300">New title: <strong>{msg.action.title}</strong></p>}
                        {msg.action.notes && (
                          <div className="mt-1.5 pt-1.5 border-t border-blue-800/30">
                            <p className="text-[10px] text-blue-400/60 uppercase tracking-wide mb-0.5">Note to append</p>
                            <p className="text-gray-200 text-xs italic">"{msg.action.notes}"</p>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2 mt-2">
                        <button onClick={() => handleUpdateTask(msg)} disabled={executingTask}
                          className="flex-1 py-1.5 text-xs font-semibold rounded-lg disabled:opacity-50 transition-colors" style={{ background: 'linear-gradient(135deg, #1a6b8a, #0d4f6b)', color: '#a8dff0', border: '1px solid #1e7a9e' }}>
                          {executingTask ? 'Updating...' : '✓ Confirm Update'}
                        </button>
                        <button onClick={() => {
                            logActionToHistory('cancelled', `Cancelled update to task "${msg.action?.task_search}"`);
                            setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, action: { ...m.action, dismissed: true } } : m));
                          }}
                          className="flex-1 py-1.5 text-xs text-gray-400 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors">
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}

        {sending && (
          <div className="flex gap-3">
            <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#1a1710' }}>
              <Sparkles className="h-3.5 w-3.5" style={{ color: '#c9a84c' }} />
            </div>
            <div className="bg-gray-900 border border-gray-800 px-4 py-3 rounded-2xl rounded-tl-sm">
              <div className="flex gap-1">
                <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ animationDelay: '0ms', background: '#c9a84c' }} />
                <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ animationDelay: '150ms', background: '#c9a84c' }} />
                <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ animationDelay: '300ms', background: '#c9a84c' }} />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Unified Duplicate Detection Card */}
      {duplicateState && (
        <div className="px-4 pb-2">
          <DuplicateCard
            state={duplicateState}
            loading={executingAction === 'create'}
            onUseExisting={handleDupUseExisting}
            onCreateAnyway={(note) => handleDupCreateAnyway(note)}
            onCancel={() => { setDuplicateState(null); setPendingCreate(null); }}
          />
        </div>
      )}

      {/* Add Contact Prompt Card */}
      {addContactPrompt && (
        <div style={{ margin: '0 16px 12px', background: '#1a1008', border: '1px solid #c9a84c', borderRadius: 10, padding: '14px 16px' }}>
          <div style={{ color: '#e5e7eb', fontSize: 13, marginBottom: 10 }}>Add <strong style={{ color: '#c9a84c' }}>{addContactPrompt.name}</strong> as a contact?</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => {
              const name = addContactPrompt.name;
              setAddContactPrompt(null);
              setInput('Create a new contact for ' + name);
            }}
              style={{ background: 'linear-gradient(135deg,#c9a84c,#a67c32)', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
              Yes, add contact
            </button>
            <button onClick={() => setAddContactPrompt(null)}
              style={{ background: 'transparent', border: '1px solid #3d2a1a', color: '#8a6540', borderRadius: 6, padding: '7px 14px', fontSize: 13, cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* DRAFT_DEMAND_LETTER Confirm Card */}
      {demandLetterState && !demandLetterOpen && selectedRecord && (
        <div style={{ margin: '0 16px 12px', background: '#111', border: '1px solid #2a2a2a', borderRadius: 10, padding: '14px 16px' }}>
          <div style={{ color: '#c9a84c', fontWeight: 700, fontSize: 13, marginBottom: 10 }}>📄 Draft Demand Letter</div>
          <p style={{ fontSize: 13, color: '#aaa', marginBottom: 14, lineHeight: 1.5 }}>{demandLetterState.action?.message || 'Generate a professional PI demand letter using all case data, medical records, parties, and documents on file.'}</p>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setDemandLetterOpen(true)} style={{ background: 'linear-gradient(135deg,#c9a84c,#a67c32)', color: '#000', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Open Letter Builder</button>
            <button onClick={() => setDemandLetterState(null)} style={{ background: 'transparent', border: '1px solid #2a2a35', color: '#6b7280', borderRadius: 6, padding: '7px 14px', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
          </div>
        </div>
      )}
      {demandLetterOpen && selectedRecord && (
        <DemandLetterModal recordId={selectedRecord.record_id} recordTitle={selectedRecord.title} onClose={() => { setDemandLetterOpen(false); setDemandLetterState(null); }} onSaved={() => { setDemandLetterOpen(false); setDemandLetterState(null); }} />
      )}
      {/* Share Case Confirm Card */}
      {shareCaseState && selectedRecord && (
        <div style={{ margin: '0 16px 12px', background: '#1a1008', border: '1px solid #c9a84c', borderRadius: 10, padding: '14px 16px' }}>
          {shareCaseState.multiple_matches ? (
            <>
              <div style={{ color: '#c9a84c', fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Multiple matches — who did you mean?</div>
              {shareCaseState.multiple_matches.map((u, idx) => (
                <button key={u.full_name + '-' + idx} onClick={() => setShareCaseState({ ...shareCaseState, recipient_name: u.full_name, recipient_full_name: u.full_name, recipient_email: u.email, recipient_type: u.type || 'user', multiple_matches: null })}
                  style={{ display: 'block', width: '100%', textAlign: 'left', background: '#2a1a0a', border: '1px solid #5a3820', borderRadius: 6, padding: '8px 12px', color: '#f5f0e8', fontSize: 13, marginBottom: 6, cursor: 'pointer' }}>
                  {u.full_name || u.name} — {u.email || 'no email'} {u.type === 'contact' ? '(contact)' : '(colleague)'}
                </button>
              ))}
              <button onClick={() => setShareCaseState(null)} style={{ marginTop: 4, background: 'transparent', border: 'none', color: '#6b6350', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
            </>
          ) : (
            <>
              <div style={{ color: '#c9a84c', fontWeight: 700, fontSize: 13, marginBottom: 6 }}>📤 Share Case {shareCaseState.recipient_type === 'contact' ? 'Info' : 'Link'}</div>
              <div style={{ color: '#9ca3af', fontSize: 11, marginBottom: 8 }}>
                {shareCaseState.recipient_type === 'contact' ? '👤 External Contact' : '👥 Colleague'}
              </div>
              {shareCaseState.recipient_type === 'contact' && !shareCaseState.recipient_email && (
                <div style={{ background: '#2a1a0a', border: '1px solid #a67c32', borderRadius: 6, padding: '8px 12px', marginBottom: 10, color: '#e5c07b', fontSize: 12 }}>
                  ⚠️ No email on file for this contact — add one first
                </div>
              )}
              <div style={{ background: '#0e0f11', border: '1px solid #2a2a35', borderRadius: 8, padding: '12px 14px', marginBottom: 12, fontSize: 12 }}>
                <div style={{ color: '#c9a84c', fontWeight: 600, marginBottom: 8 }}>📧 Email Preview</div>
                <div style={{ color: '#9ca3af', marginBottom: 4 }}>To: <span style={{ color: '#e5e7eb' }}>{shareCaseState.recipient_full_name || shareCaseState.recipient_name}{shareCaseState.recipient_email ? ' <' + shareCaseState.recipient_email + '>' : ''}</span></div>
                <div style={{ color: '#9ca3af', marginBottom: 4 }}>Subject: <span style={{ color: '#e5e7eb' }}>{shareCaseState.recipient_type === 'contact' ? (selectedRecord.title + ' — ' + selectedRecord.reference_number) : ('Case shared with you — ' + (selectedRecord.title || selectedRecord.reference_number))}</span></div>
                <div style={{ color: '#9ca3af', marginBottom: 4 }}>Case: <span style={{ color: '#c9a84c' }}>{selectedRecord.reference_number}</span> — {selectedRecord.title}</div>
                {shareCaseState.custom_message && (
                  <div style={{ color: '#9ca3af', marginBottom: 4, borderTop: '1px solid #2a2a35', paddingTop: 8, marginTop: 8 }}>Message: <span style={{ color: '#e5e7eb', fontStyle: 'italic' }}>{shareCaseState.custom_message}</span></div>
                )}
                {shareCaseState.recipient_type !== 'contact' && (
                  <div style={{ color: '#9ca3af', borderTop: '1px solid #2a2a35', paddingTop: 8, marginTop: 8 }}>Link: <span style={{ color: '#c9a84c' }}>Open in Sixty10 →</span></div>
                )}
                {shareCaseState.recipient_type === 'contact' && (
                  <div style={{ color: '#6b7280', borderTop: '1px solid #2a2a35', paddingTop: 8, marginTop: 8, fontStyle: 'italic' }}>External contact — case reference only, no app link</div>
                )}
                <div style={{ borderTop: '1px solid #2a2a35', paddingTop: 8, marginTop: 8, fontSize: 11, color: '#6b7280' }}>
                  <div style={{ color: '#9ca3af', fontWeight: 600 }}>{user?.first_name} {user?.last_name}</div>
                  {user?.job_title && <div>{user.job_title}</div>}
                  {user?.phone && <div>📞 {user.phone}</div>}
                  <div>✉ {user?.email}</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => { console.log('[SendLink] selectedRecord:', selectedRecord?.record_id, 'shareCaseState:', shareCaseState?.recipient_name); handleShareCase(selectedRecord); }}
                  disabled={shareCaseState.recipient_type === 'contact' && !shareCaseState.recipient_email}
                  style={{ background: (shareCaseState.recipient_type === 'contact' && !shareCaseState.recipient_email) ? '#555' : 'linear-gradient(135deg,#c9a84c,#a67c32)', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 13, fontWeight: 700, cursor: (shareCaseState.recipient_type === 'contact' && !shareCaseState.recipient_email) ? 'not-allowed' : 'pointer' }}>
                  ✉️ {shareCaseState.recipient_type === 'contact' ? 'Send Info' : 'Send Link'}
                </button>
                <button onClick={() => setShareCaseState(null)}
                  style={{ background: 'transparent', border: '1px solid #3d2a1a', color: '#8a6540', borderRadius: 6, padding: '7px 14px', fontSize: 13, cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Disambiguate Pick Card */}
      {disambiguateState && (
        <div style={{ margin: '0 16px 12px', background: '#1a1a2e', border: '1px solid #c9a84c', borderRadius: 10, padding: '14px 16px' }}>
          <div style={{ color: '#c9a84c', fontWeight: 700, fontSize: 13, marginBottom: 8 }}>
            🔍 {stripMarkdown(disambiguateState.reply)}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {disambiguateState.options.map((opt, i) => (
              <button key={i}
                onClick={() => handleDisambiguateSelect(opt)}
                style={{ background: '#0e0f11', border: '1px solid #3d3418', borderRadius: 8, padding: '10px 14px', textAlign: 'left', cursor: 'pointer', transition: 'border-color 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = '#c9a84c'}
                onMouseLeave={e => e.currentTarget.style.borderColor = '#3d3418'}>
                <div style={{ color: '#e5e7eb', fontWeight: 600, fontSize: 13 }}>{opt.title}</div>
                <div style={{ color: '#9ca3af', fontSize: 11, marginTop: 2 }}>
                  {opt.record_type} · {opt.reference}{opt.detail ? ' · ' + opt.detail : ''}
                </div>
              </button>
            ))}
          </div>
          <button onClick={() => setDisambiguateState(null)}
            style={{ marginTop: 10, background: 'transparent', border: 'none', color: '#6b7280', fontSize: 12, cursor: 'pointer' }}>
            Cancel
          </button>
        </div>
      )}

      {/* SMS Confirm Card */}
      {smsState && (
        <div style={{ margin: '0 16px 12px', background: '#0e1a1f', border: '1px solid #3ba3c9', borderRadius: 10, padding: '14px 16px' }}>
          <div style={{ color: '#3ba3c9', fontWeight: 700, fontSize: 13, marginBottom: 8 }}>📱 Send SMS to {smsState.recipientName}</div>
          <div style={{ background: '#0b1014', border: '1px solid #1e2a33', borderRadius: 8, padding: '10px 12px', marginBottom: 12, fontSize: 12 }}>
            {smsState.phones && smsState.phones.length > 1 ? (
              <div>
                <div style={{ color: '#9ca3af', marginBottom: 8 }}>Select number to SMS:</div>
                {smsState.phones.map((p, i) => (
                  <div key={i} onClick={() => setSmsState(prev => ({ ...prev, phone: p.number, selectedPhone: i }))}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 6, marginBottom: 4, cursor: 'pointer',
                      background: smsState.selectedPhone === i ? '#1a3548' : 'transparent',
                      border: smsState.selectedPhone === i ? '1px solid #3ba3c9' : '1px solid transparent' }}>
                    <div style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid #3ba3c9',
                      background: smsState.selectedPhone === i ? '#3ba3c9' : 'transparent' }} />
                    <span style={{ color: '#9ca3af', minWidth: 55 }}>{p.label}:</span>
                    <span style={{ color: '#e5e7eb' }}>{p.number}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: '#9ca3af', marginBottom: 4 }}>To: <span style={{ color: '#e5e7eb' }}>{smsState.phone}</span></div>
            )}
            <div style={{ color: '#9ca3af', borderTop: '1px solid #1e2a33', paddingTop: 8, marginTop: 8 }}>
              Message: <span style={{ color: '#e5e7eb', fontStyle: 'italic' }}>"{smsState.message}"</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handleSendSMS} disabled={smsSending}
              style={{ background: smsSending ? '#555' : 'linear-gradient(135deg,#3ba3c9,#2880a3)', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 13, fontWeight: 700, cursor: smsSending ? 'wait' : 'pointer' }}>
              📱 {smsSending ? 'Sending…' : 'Send SMS'}
            </button>
            <button onClick={() => setSmsState(null)} disabled={smsSending}
              style={{ background: 'transparent', border: '1px solid #1e2a33', color: '#6b7d8a', borderRadius: 6, padding: '7px 14px', fontSize: 13, cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Call Confirm Card */}
      {callState && (
        <div style={{ margin: '0 16px 12px', background: '#0f1a0f', border: '1px solid #4caf50', borderRadius: 10, padding: '14px 16px' }}>
          <div style={{ color: '#4caf50', fontWeight: 700, fontSize: 13, marginBottom: 8 }}>📞 Call {callState.recipientName}</div>
          <div style={{ background: '#0b140b', border: '1px solid #1e331e', borderRadius: 8, padding: '10px 12px', marginBottom: 12, fontSize: 12 }}>
            {callState.phones && callState.phones.length > 1 ? (
              <div>
                <div style={{ color: '#9ca3af', marginBottom: 8 }}>Select number to call:</div>
                {callState.phones.map((p, i) => (
                  <div key={i} onClick={() => setCallState(prev => ({ ...prev, phone: p.number, selectedPhone: i }))}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 6, marginBottom: 4, cursor: 'pointer',
                      background: callState.selectedPhone === i ? '#1e331e' : 'transparent',
                      border: callState.selectedPhone === i ? '1px solid #4caf50' : '1px solid transparent' }}>
                    <div style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid #4caf50',
                      background: callState.selectedPhone === i ? '#4caf50' : 'transparent' }} />
                    <span style={{ color: '#9ca3af', minWidth: 55 }}>{p.label}:</span>
                    <span style={{ color: '#e5e7eb' }}>{p.number}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: '#9ca3af', marginBottom: 4 }}>Client: <span style={{ color: '#e5e7eb' }}>{callState.phone}</span></div>
            )}
            <div style={{ color: '#9ca3af', borderTop: '1px solid #1e331e', paddingTop: 8, marginTop: 8, fontStyle: 'italic' }}>
              Your phone will ring first, then bridge to client.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handleInitiateCall} disabled={callDialing || (callState.phones && callState.phones.length > 1 && !callState.phone)}
              style={{ background: (callDialing || (callState.phones && callState.phones.length > 1 && !callState.phone)) ? '#555' : 'linear-gradient(135deg,#4caf50,#2e7d32)', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
              📞 {callDialing ? 'Dialing…' : 'Start Call'}
            </button>
            <button onClick={() => setCallState(null)} disabled={callDialing}
              style={{ background: 'transparent', border: '1px solid #1e331e', color: '#6b8a6b', borderRadius: 6, padding: '7px 14px', fontSize: 13, cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ADD_PARTY Confirm Card */}
      {addPartyState && (
        <div style={{ margin: '0 16px 12px', background: '#1a1a2e', border: '1px solid #8b5cf6', borderRadius: 10, padding: '14px 16px' }}>
          {addPartyState.notFound ? (
            <>
              <div style={{ color: '#8b5cf6', fontWeight: 700, fontSize: 13, marginBottom: 8 }}>➕ Add Party</div>
              <div style={{ background: '#2a1a0a', border: '1px solid #a67c32', borderRadius: 8, padding: '10px 12px', marginBottom: 12, fontSize: 12, color: '#e5c07b' }}>
                ⚠ "{addPartyState.contactName}" not found in contacts. Create them first, then add as party.
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => { setAddPartyState(null); setInput(`create contact ${addPartyState.contactName}`); }}
                  style={{ background: 'linear-gradient(135deg,#8b5cf6,#6d28d9)', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                  Create Contact
                </button>
                <button onClick={() => setAddPartyState(null)}
                  style={{ background: 'transparent', border: '1px solid #2a2a35', color: '#6b7280', borderRadius: 6, padding: '7px 14px', fontSize: 13, cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            </>
          ) : addPartyState.candidates ? (
            <>
              <div style={{ color: '#8b5cf6', fontWeight: 700, fontSize: 13, marginBottom: 8 }}>➕ Add Party — Which {addPartyState.contactName.split(' ')[0]}?</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
                {addPartyState.candidates.map(c => (
                  <button key={c.id} onClick={() => handleAddParty(c.id)}
                    disabled={partyExecuting}
                    style={{ background: '#0e0f11', border: '1px solid #3d3418', borderRadius: 8, padding: '8px 12px', textAlign: 'left', cursor: 'pointer', transition: 'border-color 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = '#8b5cf6'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = '#3d3418'}>
                    <div style={{ color: '#e5e7eb', fontWeight: 600, fontSize: 12 }}>{c.title}</div>
                    <div style={{ color: '#9ca3af', fontSize: 10, marginTop: 2 }}>{c.reference_number}</div>
                  </button>
                ))}
              </div>
              <div style={{ color: '#9ca3af', fontSize: 11, marginBottom: 8 }}>Role: {addPartyState.role || '—'}</div>
              <button onClick={() => setAddPartyState(null)}
                style={{ background: 'transparent', border: 'none', color: '#6b7280', fontSize: 12, cursor: 'pointer' }}>
                Cancel
              </button>
            </>
          ) : (
            <>
              <div style={{ color: '#8b5cf6', fontWeight: 700, fontSize: 13, marginBottom: 8 }}>➕ Add Party</div>
              <div style={{ background: '#0e0f11', border: '1px solid #2a2a35', borderRadius: 8, padding: '10px 12px', marginBottom: 12, fontSize: 12 }}>
                <div style={{ color: '#e5e7eb', fontWeight: 600, marginBottom: 4 }}>👤 {addPartyState.contactName}</div>
                {addPartyState.role && <div style={{ color: '#9ca3af' }}>Role: {addPartyState.role}</div>}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => handleAddParty()} disabled={partyExecuting}
                  style={{ background: partyExecuting ? '#555' : 'linear-gradient(135deg,#8b5cf6,#6d28d9)', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 13, fontWeight: 700, cursor: partyExecuting ? 'wait' : 'pointer' }}>
                  {partyExecuting ? 'Adding...' : 'Add to Case'}
                </button>
                <button onClick={() => setAddPartyState(null)} disabled={partyExecuting}
                  style={{ background: 'transparent', border: '1px solid #2a2a35', color: '#6b7280', borderRadius: 6, padding: '7px 14px', fontSize: 13, cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* REMOVE_PARTY Confirm Card */}
      {removePartyState && (
        <div style={{ margin: '0 16px 12px', background: '#1a0e0e', border: '1px solid #ef4444', borderRadius: 10, padding: '14px 16px' }}>
          <div style={{ color: '#ef4444', fontWeight: 700, fontSize: 13, marginBottom: 8 }}>🗑 Remove Party</div>
          <div style={{ background: '#0e0f11', border: '1px solid #2a2a35', borderRadius: 8, padding: '10px 12px', marginBottom: 12, fontSize: 12 }}>
            <div style={{ color: '#e5e7eb', fontWeight: 600 }}>
              Remove {removePartyState.contactName}{removePartyState.role ? ` (${removePartyState.role})` : ''} from this case?
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => handleRemoveParty()} disabled={partyExecuting}
              style={{ background: partyExecuting ? '#555' : 'linear-gradient(135deg,#ef4444,#b91c1c)', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 13, fontWeight: 700, cursor: partyExecuting ? 'wait' : 'pointer' }}>
              {partyExecuting ? 'Removing...' : 'Remove'}
            </button>
            <button onClick={() => setRemovePartyState(null)} disabled={partyExecuting}
              style={{ background: 'transparent', border: '1px solid #2a2a35', color: '#6b7280', borderRadius: 6, padding: '7px 14px', fontSize: 13, cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* MANAGE_TRIGGERS Confirm Card */}
      {triggerState && (
        <div style={{ margin: '0 16px 12px', background: '#0f1a0f', border: '1px solid #166534', borderRadius: 10, padding: '14px 16px' }}>
          <div style={{ color: '#4ade80', fontWeight: 700, fontSize: 13, marginBottom: 10 }}>⚡ Create Trigger Rule</div>
          <div style={{ fontSize: 12, color: '#d1fae5', marginBottom: 4 }}>
            <span style={{ color: '#86efac' }}>When:</span> <strong>{triggerState.field_name?.replace(/_/g,' ')}</strong> {triggerState.operator === 'any_change' ? 'changes (any value)' : `${triggerState.operator} "${triggerState.trigger_value}"`}
          </div>
          <div style={{ fontSize: 12, color: '#d1fae5', marginBottom: 4 }}>
            <span style={{ color: '#86efac' }}>Action:</span> {triggerState.action_type === 'create_task' ? 'Create task' : triggerState.action_type === 'send_email' ? 'Send email' : 'Create task + Send email'}
          </div>
          {triggerState.task_title && <div style={{ fontSize: 12, color: '#d1fae5', marginBottom: 4 }}><span style={{ color: '#86efac' }}>Task:</span> "{triggerState.task_title}"</div>}
          {triggerState.assign_to_name && <div style={{ fontSize: 12, color: '#d1fae5', marginBottom: 4 }}><span style={{ color: '#86efac' }}>Assign to:</span> {triggerState.assign_to_name}</div>}
          {triggerState.due_days_offset > 0 && <div style={{ fontSize: 12, color: '#d1fae5', marginBottom: 4 }}><span style={{ color: '#86efac' }}>Due:</span> {triggerState.due_days_offset} day(s) from trigger</div>}
          {triggerState.priority && <div style={{ fontSize: 12, color: '#d1fae5', marginBottom: 10 }}><span style={{ color: '#86efac' }}>Priority:</span> {triggerState.priority}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button disabled={triggerExecuting} onClick={async () => {
              setTriggerExecuting(true);
              try {
                const token = localStorage.getItem('lf_token');
                const res = await fetch('/api/chat-ai/create-trigger', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                  body: JSON.stringify(triggerState),
                });
                const data = await res.json();
                if (data.ok || data.rule) {
                  setMessages(prev => [...prev, { id: 'trigger-ok-' + Date.now(), role: 'assistant', content: `✅ Trigger rule created: ${triggerState.label || triggerState.task_title}`, created_at: new Date().toISOString() }]);
                } else {
                  setMessages(prev => [...prev, { id: 'trigger-err-' + Date.now(), role: 'assistant', content: `❌ ${data.error || 'Failed to create trigger'}`, created_at: new Date().toISOString() }]);
                }
              } catch(e) { console.error('[triggerState]', e); }
              finally { setTriggerExecuting(false); setTriggerState(null); }
            }} style={{ background: triggerExecuting ? '#555' : 'linear-gradient(135deg,#16a34a,#15803d)', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 13, fontWeight: 700, cursor: triggerExecuting ? 'wait' : 'pointer' }}>
              {triggerExecuting ? 'Saving...' : '⚡ Activate Trigger'}
            </button>
            <button onClick={() => setTriggerState(null)} disabled={triggerExecuting}
              style={{ background: 'transparent', border: '1px solid #2a2a35', color: '#6b7280', borderRadius: 6, padding: '7px 14px', fontSize: 13, cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {/* Input */}
      <div className="px-6 py-4 border-t border-gray-800 bg-gray-900/30">
        <div className="flex items-end gap-3 bg-gray-900 border rounded-2xl px-4 py-3 transition-colors" style={{ borderColor: '#3d3418' }}>
          <input type="file" ref={fileInputRef} onChange={handleFileUpload} className="hidden"
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt" />
          <button onClick={() => selectedRecord && fileInputRef.current?.click()}
            disabled={uploadingDoc || !selectedRecord}
            title={selectedRecord ? 'Upload document' : 'Select a case first'}
            className="p-1.5 text-gray-500 hover:text-indigo-400 disabled:text-gray-700 disabled:cursor-not-allowed transition-colors flex-shrink-0">
            <Paperclip className="h-4 w-4" />
          </button>
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={sending}
            rows={1}
            className="flex-1 bg-transparent text-gray-200 placeholder-gray-600 text-sm resize-none focus:outline-none leading-5 max-h-32 disabled:opacity-50"
            style={{ minHeight: '20px' }}
            onInput={e => { e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 128) + 'px'; }}
          />
          {speechSupported && (
            <div className="relative flex-shrink-0">
              <button onClick={toggleVoice}
                className={`p-1.5 rounded-lg transition-colors ${isListening ? 'text-red-400 bg-red-950/30' : 'text-gray-500 hover:text-gray-300'}`}
                title={isListening ? 'Stop recording' : 'Voice input'}>
                <Mic className="h-4 w-4" />
                {isListening && (
                  <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                )}
              </button>
            </div>
          )}
          <button onClick={() => handleSend()}
            disabled={!input.trim() || sending}
            className="p-1.5 disabled:text-gray-600 disabled:cursor-not-allowed transition-colors flex-shrink-0" style={{ color: '#c9a84c' }}>
            <Send className="h-4 w-4" />
          </button>
        </div>
        <p className="text-center text-gray-600 text-[10px] mt-2">AI can make mistakes. Always verify critical information.</p>
      </div>

      {/* Toast notification */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
          padding: '10px 18px', borderRadius: 12, fontSize: 13, fontWeight: 600,
          color: '#fff', animation: 'fadeIn 0.2s ease',
          background: toast.type === 'success' ? '#16a34a'
            : toast.type === 'warning' ? '#dc2626'
            : toast.type === 'error' ? '#dc2626'
            : '#c9a84c',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
        }}>
          {toast.type === 'success' && '\u2713 '}{toast.type === 'warning' && '\u26A0 '}{toast.message}
        </div>
      )}
    </div>
  );
}
// Sat Apr  4 00:11:21 UTC 2026
